/* eslint-disable max-lines */
// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapActions, mapState } from "vuex";
import alert from "../../../../../../../share/src/alert";
import loading from "../../../../../../../share/src/components/vue/ProcessingIcon";
import { ICON_KEY as BTN_ICON_KEY, BUTTON_TYPE, default as noTextSvgButton } from "../../../../../../../share/src/components/vue/NoTextSVGButton";
import { ICON_KEY as POPOVER_ICON_KEY, CONTENTS_KEY, POSITION_CLASS, default as iconPopover } from "../../../../../../../share/src/components/vue/IconPopover";
import measureList from "../../../../vue/obd/individual/dataMonitor/ObdDataMonitorMeasureList";
import dmDidDataConversion from "../../../../../share/util/didDataConversion";
import dmUtil, { TAB, DEFAULT_ZOOM_DISPLAY_VALUE, ZOOM_DISPLAY_OPTIONS } from "../../../dataMonitor/dataMonitorUtil";
import logWrapper from "../../../../../../../share/src/logWrapper";
import util from "../../../../../../../share/src/util/utils";
import { binarySearch } from "../../../../../../../share/src/util/logicUtils";
import { GraphMediator, DEFAULT_ZOOM_PAN_MODE, ZOOM_PAN_MODE_X } from "../../../../../../../share/src/util/GraphLibrary";
import { Graph, LINE_WIDTH, THIN, THICK, NORMAL, STYLE, SOLID, DASHED, DOTTED } from '../../../../../../../share/src/util/Graph';
import { TimeLine } from '../../../../../../../share/src/util/TimeLine';
import { isGraphMediatorZoomedOrPanned, linkSplitUnionMediators } from '../../../dataMonitor/dataMonitorGraphMediatorUtil';
import { applyResetZoomAction, applyZoomDisplayValueChangeAction, createZoomPanStateChangedCallback, enableZoomControlSelect } from '../../../dataMonitor/dataMonitorZoomUtil';
import diagnose from "../../../../../../../share/src/util/diagnose";
import markPage from "../../../../vue/dataMonitor/MarkPage";
import obdConfigureGraph from '../../../../vue/obd/individual/dataMonitor/ObdConfigureGraph';
import { RANGE } from "../../../dataMonitor/ConfigureGraph";
import { markRaw } from "vue";
import apiService from '../../../../../../../share/src/util/api/service';
import config from '../../../../../../../share/src/config';
import { fixNumberWithDecimal } from "../../../../../common/nodejs/mathFunctions";
import { getTranslateValue } from "./dataMonitorUtil";
import { convertKeyForPidToDid } from '../../../../../util/obd/pid';
import obdDataConversion from "./didDataConversion";

const ERR_MSG_KEY = {
  FETCH: "fetch"
};

// 結合グラフ最大項目数
export const UNION_GRAPH_MAX_LINES = 16;
// 分割グラフ最大項目数
export const SPLIT_GRAPH_MAX_LINES = 7;

const GRAPH = {
  UNION: "obd-un-grph",
  SPLIT: "obd-sp-grph_",
};

const TIMELINE = {
  UNION: "obd-un-tmln",
  SPLIT: "obd-sp-tmln",
  LIST: "obd-ls-tmln",
};

const COMPONENT_NAME = "ObdDataMonitorMeasure";

const BURST_DATA_LEN = undefined;

// 経過時間の初期値
const DEFAULT_PASSED_TIME = "00:00";

// 1秒あたりのデータポイント数
const DATA_POINTS_PER_SEC = 2000;
// グラフの最小描画間隔(ms)
const MIN_DRAW_INTERVAL = 1000;
// グラフの最大描画間隔(ms)。4000項目が2秒ごとにデータポイント来るため、その数を最大とする。
const MAX_DRAW_INTERVAL = 2000;
const PROGRESS_RESIZE_INTERVAL = 2000;
const STOPPED_RESIZE_INTERVAL = 500;

// 通常表示のグラフの太さ
const GRAPH_LINE_NORMAL = LINE_WIDTH.NORMAL;

export default {
  name: COMPONENT_NAME,
  data() {
    return {
      processing: false,
      tabs: [
        { name: "individual.tab_data_monitor_list" },
        { name: "individual.tab_data_monitor_split_graph" },
        { name: "individual.tab_data_monitor_join_graph" },
        // 他のタブを追加する場合は、この配列にオブジェクトを追加
      ],
      currentTab: TAB.LIST,
      selectedTab: 0, // 親コンポーネントからタブ操作する際に必要
      cntConverting: 0, // 作動中の変換処理数（0:false, 1~:true)
      measurementList: [], // 計測データ（リスト）
      graphCurrentTimes: [],  // グラフ摘み位置
      latestOffsetTime: 0, // 最新データ時間(ミリ秒)
      startTime: -1, // 再生ボタン押下時間
      stopTime: null, // 停止ボタン押下時間
      graphRanges: {},
      graphMediator: [],
      xRange: undefined, // x軸時間軸
      yRange: undefined, // y軸表示範囲
      lineSetting: {}, // ライン設定
      selectedGraphList: [],
      selectedGraphCheckList: [],
      selectedGraphLines: [1, 0, 0],  // tabごとのグラフ項目数
      elapsedTime: 0, // マーカ登録用の開始時間からの経過時間(ミリ秒)
      showDeleteButton: false,
      markerUuid: "",
      markerTargetTab: TAB.SPLIT,
      markerInfo: {},
      tabChanged: false,
      executionList: {}, // 表示対象リスト
      windowSize: window.innerWidth + '|' + window.innerHeight,
      baseTime: null, // 計測開始日時
      modalMark: false,
      modalConfigGraph: false,
      noSignalItems: false,
      YLabelUpdatedItems: markRaw({}), // Auto設定の場合のY軸更新済み項目
      drawInterval: MIN_DRAW_INTERVAL, // グラフの描画間隔
      loadMark: 0, // マーク登録画面の読み込みフラグ
      isAutoMarkSaving: false, // マーク自動保存中フラグ
      isFocused: false, // マークボタンフォーカス管理フラグ
      isShowListWithGraph: true, // グラフ横並び表示のリストを表示するか否か
      colorManager: null, // 色管理クラスのインスタンス
      CURSOR: config.CURSOR, // カーソル定義
      cursor: { [config.CURSOR.A]: null, [config.CURSOR.B]: null },
      // トレーニングモード用
      commentForTestMode: "", // コメント
      elapsedTimeForTestMode: 0, // 経過時間
      tagForTestMode: null, // タグ情報
      BTN_ICON_KEY, // アイコン種別の定数
      BUTTON_TYPE, // ボタン種別の定数
      POPOVER_ICON_KEY, // アイコンの種類の定数
      CONTENTS_KEY, // コンテンツの種類の定数
      POSITION_CLASS, // アイコン表示位置クラスの定数
      zoomDisplayValue: DEFAULT_ZOOM_DISPLAY_VALUE,
      zoomDisplayOptions: [...ZOOM_DISPLAY_OPTIONS],
      isApplyingZoomDisplayValue: false, // プルダウン操作時は選択値を表示し、それ以外の操作は-%に戻すための制御フラグ
      markModalProcessing: false, // マーク登録モーダルの処理中フラグ
    };
  },

  props: {
    // 項目選択データ
    selectedFunctionList: { type: Array, default: [], required: true },
    // 変換情報リスト
    conversionList: { type: Object, default: {}, required: false },
    // 計測データ
    dataMonitorList: { type: Array, default: [], required: false },
    // 計測中フラグ
    inExecution: { type: Boolean, default: false, required: true },
    // 計測データ再生中フラグ
    inPlaying: { type: Boolean, default: false, required: true },
    // 計測開始時間(ナノ秒単位のUNIXタイムスタンプ)。ただしミリ秒以下は0になるように調整
    baseOrgTime: { type: Number, default: 0, required: false },
    // 計測開始時間のミリ秒以下が0となるように、調整した時間
    baseTimeOffset: { type: Number, default: 0, required: false },
    // intdashサーバ計測UUID
    measurementUuid: { type: String, default: "", required: true },
    // intdashサーバAPIトークン
    apiToken: { type: String, default: "", required: true },
    // intdashサーバのendpoint
    endpoint: { type: String, default: "", required: true, },

    // 項目選択データ(連想配列)
    selectedFunctionHash: { type: Object, default: () => { }, required: true },
    // 変換完了通知コールバック
    completeResolve: { type: Function, default: null, required: true },
    // ActiveTestの強調表示項目リスト ※OBDでは不使用
    highlightItems: { type: Array, 'default': () => ([]), required: false },
    // 項目ごとのデータ（単位、平均、最大最小など）
    funcValues: { type: Object, default: {}, required: true },
    // 対象ECUのID。routeのStateから取得できない再生画面でのみ使用する
    specifiedSystemId: { type: String, default: "", required: false },
    // DM機能の設定インスタンス
    dataMonitorFunctionSettings: { type: Object, required: true },
    // グラフ設定情報
    graphConfigInfo: { type: Object, default: () => { }, required: false },
    // 既に保存済みのマーカー情報
    savedMarkerInfo: { type: Array, default: [], required: false },
    // 計測開始 API 取得成功 FLG
    isCompleted: { type: Boolean, require: true },
    // WS.45 と WS.125 のマーカ情報(DataMonitorMeasure.htmlで使用、OBD機能では常にdefault)
    ws45MarkerInfo: { type: Object, default: () => null, required: false },
    // 操作コントロール表示フラグ
    isShowOperationControls: { type: Boolean, default: true, required: false },
    // 全画面モーダルの表示有無
    showModal: { type: Boolean, default: false, required: false },
    // グラフ設定ボタンなどをteleportさせる行先のDOMのID
    graphSettingsDestinationId: { type: String, default: undefined, required: false },
    // HEV系ATのDM１行目にIO/Routine Control結果を表示させる対応（OBDでは不使用）
    selectedAtFunc: { type: Object, default: {}, required: false },
    hevAtMenuTitleKey: { type: String, default: "", required: false },
    isHev: { type: Boolean, default: false, required: false },
    atInfo: { type: Array, default: [], required: false },
    isActiveTestDriving: { type: Boolean, default: false, required: false },
    // (保存データ一覧) CSVの出力のみの利用か
    isOnlyCsvExport: { type: Boolean, default: false, required: false },
    // トレーニングモードか否か
    isTestMode: { type: Boolean, default: false, required: false },
  },

  beforeCreate() {
    // グラフ要素の登録
    if (!customElements.get("graph-element")) {
      customElements.define("graph-element", Graph);
    }
    // タイムライン要素の登録
    if (!customElements.get("time-line")) {
      customElements.define("time-line", TimeLine);
    }
  },

  components: {
    "processing-view": loading,
    "measure-list": measureList,
    'mark-page': markPage,
    'config-graph': obdConfigureGraph,
    'no-text-svg-button': noTextSvgButton,
    'icon-popover': iconPopover,
  },

  watch: {
    language: {
      handler() {
        const element = document.getElementById(COMPONENT_NAME + this.dataMonitorFunctionSettings.getTypeCaller());
        if (!element) {
          console.warn('Child obd element not exist');
          return;
        }
        try {
          if (!element.checkVisibility()) {
            console.warn("Child obd element is not visible.");
            return;
          }
        } catch (e) {
          console.warn(`Child obd element is not supported by user's browser. error=[${e}]`);
          return;
        }

        // グラフに渡す翻訳文字列を更新
        [TAB.UNION, TAB.SPLIT].forEach(tabId => {
          if (this.graphMediator[tabId]) {
            this.graphMediator[tabId].setTranslatedValue(
              this.$t("individual.header_data_monitor_table_max"),
              this.$t("individual.header_data_monitor_table_min"),
              this.$t("individual.header_data_monitor_table_avg"),
              this.$t("individual.header_data_monitor_table_value")
            );
          }
        });

        // 個別診断の場合と再生画面の場合でメッセージを変える
        const messageId = this.specifiedSystemId
          ? "individual.alert_data_monitor_recollection_language"
          : "individual.alert_data_monitor_measurement_language";
        // 言語変換対応
        alert.showWarning(
          this,
          null,
          this.$t(messageId),
          () => { }
        );
      },
    },

    inExecution: {
      handler(val) {
        // 実行停止
        if (!val) {
          this.isFocused = false;
          this.completeDraw();
        }

        // 計測開始
        else {
          this.initReExecute();
          // 再計測時でも凡例の更新のために、グラフの再生成を行うように空にする
          this.graphMediator.splice(0);
        }

        // 計測状態によってズーム機能の有効・無効を切り換える
        dmUtil.setGraphZoomEnabled(this.graphMediator, !val);
      }
    },

    inPlaying: {
      async handler(val) {
        // 再生停止
        if (!val) {
          if (!this.drewGraph) {
            // グラフの描画が完了していない場合、描画間隔と同じ時間待つ
            await util.sleep(this.drawInterval);
          }
          this.completeDraw();
          // マーカーをまとめて描画
          for (const marker of this.savedMarkerInfo) {
            const time = Math.floor(marker.detail.occurred_elapsed_time / 1000);
            this.setMarker(time + this.baseTime, marker.description, marker.uuid, marker.tag);
          }

          // 初期表示が分割グラフか結合グラフの場合は簡易再描画を実施
          if (this.currentTab === TAB.SPLIT || this.currentTab === TAB.UNION) {
            this.refreshCurrentTabGraphs();
          }

          // 再生停止時にズーム・パンを有効化する
          dmUtil.setGraphZoomEnabled(this.graphMediator, true);
        }
        // 再生開始
        else {
          this.currentTab = TAB.LIST;
          this.selectedTab = TAB.LIST;
          this.selectedGraphLines = [1, 0, 0];
          if (this.selectedGraphCheckList.length > 0) {
            // チェックボックス初期化
            this.selectedGraphCheckList = this.selectedFunctionList.map((x) => {
              // 初期値は選択項目0件とする
              const values = [false, false];
              return { data_monitor_id: x.data_monitor_id, checkValue: values, menu_title: x.menu_title };
            });
            // チェックされた数も0件になったことを親に通知する
            this.$emit("change-check-value", 0);
          }

          // チェックボックス初期化に伴い、使用中の色もクリアする
          this.colorManager = new dmUtil.ColorManager();

          this.initReExecute();
          this.drewGraph = false;
          // ツリーによるシングルページ内の画面切り替えでも凡例の更新のために、グラフの再生成を行うように空にする
          this.graphMediator.splice(0);
        }
      }
    },

    dataMonitorList: {
      // 取得データに変更があったら変換待ちに格納
      immediate: true,
      handler(val) {
        if (val && val.length) {
          this.consumeQueue(val);
        }
        if (this.completeResolve) {
          this.completeResolve();
        }
      }
    },

    selectedFunctionList: {
      immediate: true,
      handler(value) {
        // 項目数に応じて、データポイントが来る間隔を基準に描画間隔を変える
        const dataPointPeriod = Object.keys(value).length / DATA_POINTS_PER_SEC * 1000;
        if (dataPointPeriod < MIN_DRAW_INTERVAL) {
          this.drawInterval = MIN_DRAW_INTERVAL;
        } else if (MAX_DRAW_INTERVAL < dataPointPeriod) {
          this.drawInterval = MAX_DRAW_INTERVAL;
        } else {
          // 100ms単位で切り上げ
          this.drawInterval = Math.ceil(dataPointPeriod / 100) * 100;
        }
      }
    },

    selectedFunctionHash: {
      // 項目選択データ(連想配列)が更新されたらデータをクリア
      handler() {
        this.clearData();
      }
    },

    conversionList: {
      // 変換情報リストが更新されたらデータをクリア
      handler() {
        this.clearData();
        // 再生画面はツリー遷移の場合に変換情報の項目が変わるため、Y軸設定の更新後にグラフ初期設定処理を実施する
        !this.inPlaying && this.mountedLogic();
      }
    },

    currentTab: {
      handler(tab) {
        tab;
        try {
          this.tabChanged = true;
          this.$emit('current-tab-changed', tab);
          this.$emit('zoom-pan-state-changed', this.isCurrentTabZoomedOrPanned(tab));
          const interval = this.inProgress() ? PROGRESS_RESIZE_INTERVAL : STOPPED_RESIZE_INTERVAL;
          // タブが切り替わったら、リサイズとラインの引き直しを行う
          setTimeout(() => {
            this.resizeAndRedraw(tab);
          }, interval);
        } catch (e) {
          e;
        }
      }
    },

    baseOrgTime: {
      handler(time) {
        if (!time) {
          this.noSignalItems = true;
          return;
        }

        if (this.inPlaying && !this.graphMediator.length) {
          // 再生画面は変換情報更新によるY軸設定の更新後にグラフ初期設定処理を実施するため、基準時刻のイベントで実施する
          this.mountedLogic();
        }

        this.baseTime = time;

        // 計測開始時間を設定する
        if (this.graphMediator[TAB.LIST]) {
          this.graphMediator[TAB.LIST].setBaseTime(this.baseTime, this.baseTimeOffset);
        }
        if (this.graphMediator[TAB.UNION]) {
          this.graphMediator[TAB.UNION].setBaseTime(this.baseTime, this.baseTimeOffset);
        }
        if (this.graphMediator[TAB.SPLIT]) {
          this.graphMediator[TAB.SPLIT].setBaseTime(this.baseTime, this.baseTimeOffset);
        }
      }
    },

    showModal: {
      immediate: true,
      handler() {
        // 最大・通常状態の描画後、描画領域に合わせてリサイズとラインの引き直しを行う
        this.$nextTick(() => {
          this.resizeAndRedraw(this.currentTab);
        });
      }
    },

    latestOffsetTime: {
      handler() {
        if (!this.inExecution && !this.inPlaying && (this.drawTimer === null || this.drawTimer === undefined)) {
          // 計測・再生停止後で描画タイマーも止まった後に、最終時刻が更新された場合、グラフ描画を実施
          this.drawGraph();
          // グラフの開始・終了時刻を更新
          [TAB.SPLIT, TAB.UNION].forEach(tabId => {
            this.graphMediator[tabId].updateGraphRange(this.startTimeVal(), this.endTimeVal());
          });
        }
      }
    },
  },

  computed: {
    ...mapState({
      language: (state) => state.systems.language,
      individualMenus: (state) => state.vehicle.individualMenus,
      userId: (state) => state.userProperty.userId || '', // ログアウト直後のレンダリングでwarningログがでるため、取得できなければ空
      systemId(state) {
        if (this.specifiedSystemId) {
          return this.specifiedSystemId;
        }
        const currentSystemNo = this.$route.params.systemNo;
        const obdSystems = state.obd.systems;
        const currentSystem = (currentSystemNo && obdSystems?.length) ? obdSystems.find(system => system.systemNo === currentSystemNo) : null;
        return currentSystem?.diagnosticSystemId ? currentSystem.diagnosticSystemId : null;
      },
      projectDataSource: state => state.cache.projectDataSource,
      // 診断系のAPIは複数画面から使用されているため
      operationScreen() {
        return diagnose.operationFrom(this.$router.currentRoute.value.name);
      },
    }),

    // 経過時間表示
    currentTimeStr() {
      let time = null;

      // カレント日時(計測後はスライダーが移動できるため、タブによって日時が異なる可能性がある)
      const tmpTime = this.graphCurrentTimes[this.currentTab];

      // 計測中は最後に計測された経過時間を表示
      if (this.inExecution || tmpTime === 0 || this.baseTime === null) {
        time = this.latestOffsetTime;
      }
      // 停止後は「計測開始日時 - カレント日時」を表示
      else {
        time = tmpTime - this.baseTime;
      }

      // 基準時刻または経過時間が未設定の場合はデフォルトを表示
      if (!this.baseTime || !time) {
        return DEFAULT_PASSED_TIME;
      }
      // 経過時間をフォーマットして表示
      else {
        return GraphMediator.getPassedTimeStr(time, false);
      }
    },

    btnDisabled() {
      return {
        seekBtns: this.inExecution || this.inPlaying,
        cursorBtns: this.inExecution || this.inPlaying,
        // Backendの開始が完了するまで停止は押せないようにする
        execute: this.inPlaying || (!this.isCompleted && this.inExecution),
        //play: true,
        //stop: true,
        // 再生画面では全データ取り切るまでマーカーボタンは無効化
        marker: (this.measurementUuid === "" || this.latestOffsetTime <= 0)
          || (this.specifiedSystemId !== "" && this.inPlaying),
        help: this.inExecution || this.inPlaying,
      };
    },

    /**
     * 倍率変更プルダウンの表示有無を切り替える
     * @returns {boolean} 倍率変更プルダウンを表示する場合はtrue、非表示の場合はfalseを返す。
     */
    enableZoomControlSelect() {
      return enableZoomControlSelect(this.currentTab, this.inExecution, this.inPlaying, this.dataMonitorFunctionSettings.isWsExe());
    },


    tabList() {
      return TAB.LIST;
    },

    tabSplit() {
      return TAB.SPLIT;
    },

    tabUnion() {
      return TAB.UNION;
    },

  },

  methods: {
    ...mapActions("systems", ["setLanguage"]),
    ...mapActions("app", ["setViewProcessMode"]),

    /**
     * ズーム倍率変更時のハンドラー
     * @param {String} value 選択されたズーム倍率の値 (例: "100%")
     */
    handleZoomDisplayValueChange(value) {
      applyZoomDisplayValueChangeAction(this, value);
    },

    /**
     * vue.jsライフサイクル（mounted）もしくはconversionList更新時の処理を行う
     */
    mountedLogic() {
      const self = this;
      // 変換済計測データを初期化
      self.convertedData = {};
      try {
        this.noSignalItems = false;
        // 項目選択リスト未取得の場合は取得後に処理を行うため処理を中断する。
        if (self.selectedFunctionList.length <= 0) {
          this.noSignalItems = true;
          return;
        }
        // 変換リスト未取得の場合は取得後に処理を行うため処理を中断する。
        if (Object.keys(self.conversionList).length <= 0) {
          return;
        }
        // グラフライブラリインスタンスが既にある場合は処理を中断する。
        if (self.graphMediator.length > 0) {
          return;
        }
      } finally {
        this.$emit('change-no-signal', this.noSignalItems);
      }

      if (self.selectedGraphCheckList.length <= 0) {
        // チェックボックス初期化
        self.selectedGraphCheckList = self.selectedFunctionList.map((x) => {
          // 初期値は選択項目0件とする
          const values = [false, false];
          return { data_monitor_id: x.data_monitor_id, checkValue: values, menu_title: x.menu_title };
        });
      }

      // グラフライブラリインスタンス格納メンバ変数をタブ毎に用意。
      self.graphMediator.push(null);
      self.graphMediator.push(new GraphMediator());
      self.graphMediator.push(new GraphMediator());
      linkSplitUnionMediators(self.graphMediator);

      const zoomPanStateChangedCallback = createZoomPanStateChangedCallback(this);
      self.graphMediator[TAB.SPLIT].updateZoomPanStateChangedCallback(zoomPanStateChangedCallback);
      self.graphMediator[TAB.UNION].updateZoomPanStateChangedCallback(zoomPanStateChangedCallback);

      // ズーム/パンの初期状態（未操作）を親へ通知する
      zoomPanStateChangedCallback(false);

      // 各タブ毎にマーカエレメント保持用のメンバ変数を用意
      self.markerElementArray = [];
      self.markerElementArray.push([]);
      self.markerElementArray.push([]);
      self.markerElementArray.push([]);

      // グラフカレント時刻格納用メンバ変数用意
      self.graphCurrentTimes.push(-1);
      self.graphCurrentTimes.push(-1);
      self.graphCurrentTimes.push(-1);

      // タイムラインシークバー摘み移動時のコールバック関数設定
      self.$refs.splitGraphTimeLineSeekBar._handleChange = self.graphTimelineSeek;
      self.$refs.splitGraphTimeLineSeekBar._handleRangeChange = self.graphTimelineRangeChange;
      self.$refs.unionGraphTimeLineSeekBar._handleChange = self.graphTimelineSeek;
      self.$refs.unionGraphTimeLineSeekBar._handleRangeChange = self.graphTimelineRangeChange;

      // リスト用グラフライブラリ初期設定処理
      self.setGraph(TAB.LIST);

      // 分割グラフ用グラフライブラリ初期設定処理
      self.setGraph(TAB.SPLIT);

      // 結合グラフ用グラフライブラリ初期設定処理
      self.setGraph(TAB.UNION);

      // ウィンドウリサイズイベントのリスナー（複数登録を防ぐため、一旦削除して登録）
      window.removeEventListener('resize', this.handleResize);
      window.addEventListener('resize', this.handleResize);

      // 再計測など既にグラフが選択されている場合は、色の使用状況はそのまま(グラフのdatasetは残っているため)
      if (!this.colorManager) {
        this.colorManager = new dmUtil.ColorManager();
      }
    },

    // ウィンドウリサイズイベントのハンドラー
    handleResize() {
      // 計測中は2000ms、停止後は500ms以内に発生した最後のイベントの発生後のみ処理を実行
      const interval = this.inProgress() ? PROGRESS_RESIZE_INTERVAL : STOPPED_RESIZE_INTERVAL;

      clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => {
        // DataMonitorMeasureList.jsにウィンドウサイズ変更を通知する
        this.windowSize = window.innerWidth + '|' + window.innerHeight;

        // 計測中は、表示中のグラフのサイズの変更のみ行う(CPU負荷軽減)。停止後はどちらのグラフも、マーカー含めて全体の更新を行う
        this.resizeAndRedraw(this.currentTab);
      }, interval);
    },

    /**
     * 計測中 or 変換中
     * @returns
     */
    inProgress() {
      return this.inExecution || this.inPlaying || this.cntConverting > 0;
    },

    /**
     * 合計時間
     * @returns
     */
    totalTime() {
      // 時間リストの最後の計測時間を返却
      return this.latestOffsetTime;
    },

    /**
     * 選択項目データ以外を削除
     */
    clearData() {
      this.convertedData = {};
      this.measurementList = [];
      this.latestOffsetTime = 0;
      this.graphRanges = {};
      this.eventDelete = null;
      this.baseTime = null;
      this.YLabelUpdatedItems = {};
    },

    /**
     * 計測項目一覧の多言語対応
     */
    changeDmi18nSelectedFunctionList() {
      const list = this.selectedFunctionList.map((item) => {
        const i18nDmItemKey = `pid.${convertKeyForPidToDid(item.data_monitor_id)}`;
        return {
          data_monitor_id: item.data_monitor_id,
          menu_title: util.trimStr(this.$t(i18nDmItemKey)),
          pullDownList: item.pullDownList,
          pullDownVisible: item.pullDownVisible,
          selectItem: item.selectItem,
          sort_id: item.sort_id,
        };
      });

      // グラフ設定用の項目を変換
      if (this.selectedGraphCheckList && this.selectedGraphCheckList.length > 0) {
        for (let num = 0; num < list.length; num++) {
          this.selectedGraphCheckList[num].menu_title = list[num].menu_title;
        }
      }

      this.$emit("change-selected-funcs", list);
    },

    /**
     * 変換待ちデータ変換
     */
    consumeQueue(val) {
      function getDataCount(convertedData) {
        if (!convertedData || !Object.keys(convertedData).length) {
          return null;
        }
        // グラフに描画する最大数分だけの数を確認
        return Object.keys(convertedData).slice(0, UNION_GRAPH_MAX_LINES)
          .reduce((acc, current) => acc + convertedData[current].length, 0);
      }

      // baseTimeがセットされていない場合はスキップ
      if (!this.baseTime) {
        return;
      }

      this.cntConverting++;
      // 変換待ちデータ取得
      const preConversion = val;

      if (!(this.convertedData)) {
        this.convertedData = {};
      }

      let dataTimeStamp;
      for (const data of preConversion) {
        if (!data || !data.length) {
          return;
        }

        for (const convertedModel of data) {
          const { data_monitor_id, ...conv } = convertedModel;

          // 変換済みデータ、計測時間を計測データリストに追加
          if (!(convertedModel.data_monitor_id in this.convertedData)) {
            this.convertedData[convertedModel.data_monitor_id] = [];
          }
          this.convertedData[data_monitor_id].push(conv);

          dataTimeStamp = convertedModel.time;
        }
      }

      // カレント位置を保持する
      this.graphCurrentTimes[TAB.LIST] = dataTimeStamp;
      this.graphCurrentTimes[TAB.SPLIT] = dataTimeStamp;
      this.graphCurrentTimes[TAB.UNION] = dataTimeStamp;
      this.graphCurrentTimes = [...this.graphCurrentTimes];

      // シークバーのカレント位置をデータポイントの時間に設定する
      this.graphMediator[TAB.LIST].timelineElement.currentTimeMs = dataTimeStamp;
      this.graphMediator[TAB.SPLIT].timelineElement.currentTimeMs = dataTimeStamp;
      this.graphMediator[TAB.UNION].timelineElement.currentTimeMs = dataTimeStamp;

      // 経過時間
      this.latestOffsetTime = dataTimeStamp - this.baseTime;

      // 最新データのみを表示データとして設定
      this.executionList = Object.freeze(this.getLastMeasurementItems(this.convertedData));

      this.cntConverting--;

      if ((this.inExecution || this.inPlaying) && ((this.drawTimer === null) ||
        (this.drawTimer === undefined))) {

        // 描画インターバルタイマ開始
        this.drawTimer = true;
        const self = this;

        (function loop() {
          setTimeout(() => {
            if ((self.drawTimer === null) || (self.drawTimer === undefined) || !self.graphMediator.length) {
              // キャンセル済、または別の再生が開始された場合なら終了
              return;
            }
            const beforeCount = getDataCount(self.convertedData);

            // グラフ描画
            self.drawGraph();

            const afterCount = getDataCount(self.convertedData);
            // 描画処理中に新しく描画対象が増えているか否か
            self.drewGraph = beforeCount !== null && (beforeCount === afterCount);

            loop();
          }, self.drawInterval);
        })();
      }

    },

    /**
     * グラフの描画
     */
    drawGraph() {
      // 再生データ作成に進む
      this.createPlayData();

      // CSVのエクスポートのみの場合は描画設定をしない
      if (this.isOnlyCsvExport) {
        return;
      }

      // タイムラインのマーカをアップデート
      if (this.currentTab === TAB.LIST && this.graphMediator[TAB.LIST]) {
        const timeElement = this.graphMediator[TAB.LIST].timelineElement;
        timeElement.updateMarker(timeElement.index);
        timeElement.updateComponent();
      }

      // 分割グラフのマーカをアップデート
      this.graphMediator[TAB.SPLIT] && this.graphMediator[TAB.SPLIT].graphElementList.forEach((g) => {
        g.updateMarker();
      });

      // 結合グラフのマーカをアップデート
      this.graphMediator[TAB.UNION] && this.graphMediator[TAB.UNION].graphElementList.forEach((g) => {
        g.updateMarker();
      });
      this.resize();
    },

    /**
     * 計測中の最新データ取得
     * @returns {object} newData 計測データ
     */
    getLastMeasurementItems(newData) {
      const items = {};
      for (const func of this.selectedFunctionList) {
        if (!(func.data_monitor_id in newData)) {
          continue;
        }

        // 最新データを抽出
        items[func.data_monitor_id] = newData[func.data_monitor_id][newData[func.data_monitor_id].length - 1];
      }

      return items;
    },

    /**
     * 再生データの作成
     */
    createPlayData() {
      this.genMeasurement(TAB.SPLIT);
      this.genMeasurement(TAB.UNION);
      // 計測中・変換中・変換待ちデータがあればreturn
      if (this.inProgress()) return;
    },

    /**
     * グラフへの設定データの作成
     * @param tabId
     */
    genMeasurement(tabId) {
      const self = this;
      if (self.graphMediator.length <= 0) {
        return;
      }

      const dataLists = self.genMeasurementGraph(tabId);
      if (tabId === TAB.UNION) {
        if (Object.keys(dataLists.adddatapointdt).length > 0) {
          const brstLen = (BURST_DATA_LEN === undefined) ? BURST_DATA_LEN : Math.floor(BURST_DATA_LEN / Object.keys(dataLists.adddatapointdt).length);
          const rc = self.genSubDivisionMeasurement(dataLists.adddatapointdt, brstLen);
          rc.forEach((d) => {
            try {
              self.graphMediator[tabId].addDataPoint(self.unionGraphId(), d);
              if (self.currentTab === tabId) {
                self.graphMediator[tabId].updateComponent();
              }
            } catch (e) {
              logWrapper.log('addDataPoint error: ', e);
            }
          });
        }
      } else {
        const rc = self.genSubDivisionMeasurement(dataLists.adddatapointdt, BURST_DATA_LEN);
        rc.forEach((d) => {
          for (let num = 0; num < self.selectedFunctionKeys().length; num++) {
            try {
              self.graphMediator[tabId].addDataPoint(self.splitGraphId(num + 1), d);
              if (self.currentTab === tabId) {
                self.graphMediator[tabId].updateComponent();
              }
            } catch (e) {
              logWrapper.log('addDataPoint error: ', e);
            }
          }
        });
      }
    },

    /**
     * グラフへの設定データ数を制限する
     * @param dataPoints
     * @param brstLen
     * @returns 数制限したグラフへの設定データ
     */
    genSubDivisionMeasurement(dataPoints, brstLen) {
      const rc = [];
      if (brstLen === undefined) {
        rc.push(dataPoints);
        return rc;
      }
      let tmpDataPoints = dataPoints;
      const each_max_len = Math.max(...Object.keys(tmpDataPoints)
        .map((name) => Object.keys(tmpDataPoints[name]).length));
      for (let i = 0; i < each_max_len; i += brstLen) {
        tmpDataPoints = dataPoints;
        tmpDataPoints = Object.keys(tmpDataPoints).reduce((acc0, key) => {
          const arr = Object.keys(tmpDataPoints[key]).slice(i, i + brstLen);
          if (arr.length > 0) {
            acc0[key] = arr.reduce((acc1, key1) => {
              acc1[key1] = Number(tmpDataPoints[key][key1]);
              return acc1;
            }, {});
          }
          return acc0;
        }, {});
        rc.push(tmpDataPoints);
      }
      return rc;
    },

    /**
     * グラフデータ作成
     * @returns グラフライブラリに渡すデータリスト
     */
    genMeasurementGraph(tab) {
      const self = this;
      const resultList = { addnewgraphopt: {}, adddatapointdt: {} };
      if (!(String(tab) in self.graphRanges)) {
        self.graphRanges[String(tab)] = {};
      }

      // 対象のグラフ項目のみ描画する
      // 初期値は選択項目0件とする
      let selectedFuncs = [];
      if (self.selectedGraphCheckList.length > 0) {
        const type = (tab === TAB.SPLIT) ? 0 : 1;
        selectedFuncs = self.selectedGraphCheckList.filter((x) => x.checkValue[type]);
      }
      if (selectedFuncs.length === 0) {
        return resultList;
      }

      for (const id of Object.keys(self.convertedData)) {
        // 表示最大描画数をチェック→最大を超えたデータは捨てる
        const selectFunc = selectedFuncs.find((x) => x.data_monitor_id === id);
        if (!selectFunc) {
          continue;
        }
        if (!(id in self.graphRanges[String(tab)])) {
          self.graphRanges[String(tab)][id] = {};
        }

        // グラフにaddDataPointするデータを作成
        const addPointKey = selectFunc.data_monitor_id;
        resultList.adddatapointdt[addPointKey] = {};
        // 時刻
        const lastTime = self.graphRanges[String(tab)][id].drawLastTime;
        for (const currentItem of self.convertedData[id]) {
          if (lastTime !== undefined && Number(currentItem.time) <= lastTime) {
            // 描画済みのデータポイントの場合は次へ
            continue;
          }

          // データポイント
          resultList.adddatapointdt[addPointKey][currentItem.time] = this.convertDataPointValue(id, currentItem.convertedData);
        }

        const yAxesVal = self.getYAxesMinMaxValue(id);
        const min = yAxesVal.min;
        const max = yAxesVal.max;

        // グラフ登録済なら最終時刻を更新するのみ
        const latest = self.convertedData[id].length ? self.convertedData[id][self.convertedData[id].length - 1].time : 0;
        const first = self.convertedData[id].length ? self.convertedData[id][0].time : 0;
        if (self.graphRanges[String(tab)][id].drawLastTime !== undefined) {
          // 最終時刻更新
          if (self.graphRanges[String(tab)][id].drawLastTime < latest) {
            self.graphRanges[String(tab)][id].drawLastTime = latest;
          }
          // 開始時刻更新
          if (self.graphRanges[String(tab)][id].drawFirstTime > first) {
            self.graphRanges[String(tab)][id].drawFirstTime = first;
          }
          continue;
        }
        // グラフ未登録ならaddNewGraph()を行うため必要な情報を用意する
        self.graphRanges[String(tab)][id].drawLastTime = latest;
        self.graphRanges[String(tab)][id].drawFirstTime = first;
        resultList.addnewgraphopt = {};
        const conversion = util.isNullOrDefault(self.conversionList[id].conversion);
        resultList.addnewgraphopt[addPointKey] = {
          unit: conversion !== null ? self.$t(`obd_unit.` + self.conversionList[id].conversion.unit) : '-',
          min: min,
          max: max,
          startTime: self.graphRanges[String(tab)][id].drawFirstTime,
        };
      }

      return resultList;
    },

    /**
     * グラフに渡すデータポイントに変換する
     * @param {*} id 翻訳キー
     * @param {*} convertedData 変換済データ
     * @returns
     */
    convertDataPointValue(id, convertedData) {
      let dataPointValue = [];
      // convertedDataに値が入っていない場合
      if (convertedData === undefined || convertedData === null) {
        return dataPointValue;
      }

      if (dmUtil.VALUE_TRANSLATE_TYPE.includes(this.conversionList[id].data_type)) {
        // ツールチップや凡例の表示に、翻訳ファイルの翻訳結果を使用するデータタイプ
        const translated = getTranslateValue(this, id, convertedData, this.conversionList[id]);

        if (this.conversionList[id].data_type === "FLG") {
          dataPointValue = [convertedData === "TRUE" ? 255 : 0, translated];
        } else {
          // OBDのHEXは翻訳キー取得のため、bitの場合は2進数で変換値を保持
          if (this.conversionList[id].byte_or_bit === "byte") {
            dataPointValue = [parseInt(convertedData, 16), translated];
          } else {
            dataPointValue = [parseInt(convertedData, 2), translated];
          }
        }
      } else if (dmUtil.VALUE_CONVERT_DECIMAL_TYPE.includes(this.conversionList[id].data_type)) {
        // ツールチップや凡例の表示は物理変換値、グラフをプロットする値は10進数変換した値を使用するデータタイプ
        const displayValue = obdDataConversion.convertDataToDisplayValue(convertedData, this.conversionList[id]);
        const intValue = dmDidDataConversion.convertBinToInt(convertedData, this.conversionList[id]);
        dataPointValue = [intValue, displayValue];
      } else {
        // ツールチップや凡例の表示と、グラフをプロットする値を使用するデータタイプ
        dataPointValue = [convertedData, null];
      }

      return dataPointValue;
    },

    /**
     * Y軸情報のmin/maxを取得
     * @param {string} id data_monitor_id
     * @param {boolean} addNewGraph グラフ追加を示すフラグ
     * @returns min値、max値
     */
    getYAxesMinMaxValue(id, addNewGraph = false) {
      const self = this;
      const conversion = self.conversionList[id];
      const funcValue = self.funcValues[id];
      const yRangeInfo = self.graphConfigInfo.yRange[id];
      let min, max, minDisp, maxDisp;

      // データタイプがValueとdecExStrの場合以外は、グラフの最大最小はchart.jsの自動設定で描画
      if (['Value', 'decExStr'].includes(conversion.data_type)) {
        const digit = conversion.conversion.digit_after_decimal;
        // yRangeの設定があり、且つAUTO以外が設定されている場合は設定値を利用する
        if (yRangeInfo && yRangeInfo.selectedRange !== RANGE.AUTO) {
          min = yRangeInfo.min;
          max = yRangeInfo.max;
          minDisp = fixNumberWithDecimal(min, digit);
          maxDisp = fixNumberWithDecimal(max, digit);
        } else if (funcValue?.min !== undefined && funcValue?.max !== undefined) {
          // AUTO設定ではグラフの最大・最小の設定とY軸ラベルを動的に設定
          min = Number.isNaN(funcValue.min) ? undefined : funcValue.min;
          max = Number.isNaN(funcValue.max) ? undefined : funcValue.max;
          minDisp = funcValue.minDisp;
          maxDisp = funcValue.maxDisp;
          if (!yRangeInfo) {
            // yRangeが存在しない場合、新しく定義する
            self.graphConfigInfo.yRange[id] = {
              min: conversion.conversion.graph_range_display.min,
              max: conversion.conversion.graph_range_display.max,
              minDisp: fixNumberWithDecimal(conversion.conversion.graph_range_display.min, digit),
              maxDisp: fixNumberWithDecimal(conversion.conversion.graph_range_display.max, digit),
              selectedRange: RANGE.AUTO
            };
          }

          if (addNewGraph) {
            this.YLabelUpdatedItems[id] = false;
          }
          this.updateYInfo(id, min, max, minDisp, maxDisp, addNewGraph);
        }
      } else if (funcValue?.min !== undefined && funcValue?.max !== undefined) {
        // グラフ描画するそれ以外のデータタイプは、計測値の最大と最小をY軸の最大と最小にするが、ラベルはハイフン固定
        min = funcValue.min;
        max = funcValue.max;
        minDisp = undefined;
        maxDisp = undefined;
        if (addNewGraph) {
          this.YLabelUpdatedItems[id] = false;
        }
        this.updateYInfo(id, "-", "-", "-", "-", addNewGraph);
      }

      if (min >= max) {
        // AUTO設定とする
        min = undefined;
        max = undefined;
      }
      return { min: min, max: max, minDisp: minDisp, maxDisp: maxDisp };
    },

    /**
     * Y軸情報の更新
     * @param {string} id data_monitor_id
     * @param {number} min 最小値
     * @param {number} max 最大値
     * @param {string} minDisp 最小値の表示文字列
     * @param {string} maxDisp 最大値の表示文字列
     * @param {boolean} addNewGraph グラフ追加を示すフラグ
     */
    updateYInfo(id, min, max, minDisp, maxDisp, addNewGraph = false) {
      if (!this.graphMediator.length) {
        // グラフライブラリインスタンス作成前の場合はラベルの更新をせずに処理を終了
        return;
      }

      if (!this.yRange) {
        // 再表示時は、グラフ設定ボタンを押下前は親コンポーネントのY軸情報のみあるため、自コンポーネントが持つY軸情報にも設定する
        this.yRange = this.graphConfigInfo.yRange;
      }

      // 最大値と最小値が同じ場合は、Y軸ラベルのみ設定する
      if (min === max) {
        if (this.YLabelUpdatedItems[id]) {
          // 更新済みであれば更新しない
          return;
        }
        const splitElementList = this.graphMediator[TAB.SPLIT].getGraphElementList();
        const unionElementList = this.graphMediator[TAB.UNION].getGraphElementList();
        const splitGraphElement = splitElementList.find(element => (element._yAxesInfo[0] && element._yAxesInfo[0].id) === id);
        const unionGraphElement = unionElementList[0];

        if (!splitGraphElement && !unionGraphElement) {
          // グラフ初期化前の場合はラベルの更新をせずに処理を終了
          return;
        }

        [[splitGraphElement, TAB.SPLIT], [unionGraphElement, TAB.UNION]].forEach(([graphElement, tabId]) => {
          const yAxisInfo = graphElement && graphElement._yAxesInfo.find(yAxis => yAxis.id === id);
          if (yAxisInfo) {
            const lastInd = yAxisInfo.lastIndex;
            const displayMin = graphElement.formatYLabelText(minDisp);
            const displayMax = graphElement.formatYLabelText(maxDisp);
            this.graphMediator[tabId].updateYLabel(graphElement.id, lastInd, displayMin, displayMax);
            // yRangeSettingで全yRangeの最大・最小のテキストの更新を行うため、Value等yRange設定がある項目は、min/maxが同じ場合でもminDispとmaxDispを設定する
            if (this.yRange[id]) {
              this.yRange[id] = { ...this.graphConfigInfo.yRange[id], minDisp: minDisp, maxDisp: maxDisp };
            }
            // 新規追加の場合はグラフ作成後に更新済みにする
            if (!addNewGraph) {
              // このIDの項目はY軸情報を更新済みにする
              this.YLabelUpdatedItems[id] = true;
            }
          }
        });
      }
      // 最大値か最小値が変わった場合は、グラフのY軸情報とY軸ラベルを更新する
      else if (this.graphConfigInfo.yRange[id].min !== min || this.graphConfigInfo.yRange[id].max !== max) {
        const elementList = this.graphMediator[TAB.SPLIT].getGraphElementList();
        const graphIndex = elementList.findIndex(element => (element._yAxesInfo[0] && element._yAxesInfo[0].id) === id);

        this.yRange[id] = { ...this.graphConfigInfo.yRange[id], min: min, max: max, minDisp: minDisp, maxDisp: maxDisp };

        // updateYInfoで更新する値のみyRangeSettingの第2引数に渡す
        // this.yRangeを渡すとグラフ表示対象として選択したAUTOの項目の情報が残っている場合、その値はundefinedでmin/maxが更新されてしまう
        this.graphMediator[TAB.SPLIT].yRangeSetting(this.splitGraphId(graphIndex + 1), { [id]: this.yRange[id] });
        this.graphMediator[TAB.UNION].yRangeSetting(this.unionGraphId(), { [id]: this.yRange[id] });
        // 親コンポーネントがもつY軸情報も更新
        this.$emit('set-graph-config', this.xRange, this.yRange, this.lineSetting);
      }
    },

    /**
     * グラフ設定ボタン押下時処理
     */
    handleConfigureGraph() {
      const list = [];
      for (const item of this.selectedGraphCheckList) {
        // 分割G、結合Gにチェックされている項目を対象とする
        if (item.checkValue[0] || item.checkValue[1]) {
          list.push(item);
        }
      }
      this.selectedGraphList = list;
      // 設定済みの値があれば更新する
      this.xRange = (this.graphConfigInfo.xRange !== 0) ? this.graphConfigInfo.xRange : undefined;
      this.yRange = (Object.keys(this.graphConfigInfo.yRange).length > 0) ? this.graphConfigInfo.yRange : undefined;
      if (this.currentTab !== TAB.LIST) {
        Object.keys(this.lineSetting).forEach((did) => {
          if (!this.lineSetting[did].colorChanged && this.lineSetting[did].borrowColor && this.lineSetting[did].borrowColor[this.currentTab])
            this.lineSetting[did].color = this.lineSetting[did].borrowColor[this.currentTab];
        });
      }
      this.modalConfigGraph = true;
    },

    /**
     * ズームリセットボタン押下時処理
     */
    handleResetZoom() {
      applyResetZoomAction(this);
    },

    /**
     * 現在表示中のグラフがズーム/パン状態か否か
     * @param {number} tabId タブID
     * @returns {boolean} 現在表示中のグラフがズーム/パン状態の場合はtrue
     */
    isCurrentTabZoomedOrPanned(tabId = this.currentTab) {
      return isGraphMediatorZoomedOrPanned(this.graphMediator, tabId);
    },

    /**
     * グラフ設定で表示する文字列の翻訳
     * @returns 翻訳後の文字列
     */
    getDisplayPartsConfigureGraph() {
      return {
        TITLE: this.$t("individual.label_data_monitor_graph_config"),
        RANGE: {
          tab: this.$t("individual.tab_data_monitor_display_range"),
          itemLabel: this.$t("individual.label_data_monitor_item"),
          rangeLabel: this.$t("individual.label_data_monitor_display_range"),
          optionLabel: {
            defaultRange: this.$t("individual.label_data_monitor_default_range"),
            autoRange: this.$t("individual.label_data_monitor_auto_range"),
            manRange: this.$t("individual.label_data_monitor_manual_range"),
          },
          manualMinLabel: this.$t("individual.label_data_monitor_range_min"),
          manualMaxLabel: this.$t("individual.label_data_monitor_range_max"),
        },
        TIME_GRID: {
          tab: this.$t("individual.tab_data_monitor_time_axis"),
          label: this.$t("individual.label_data_monitor_time_axis"),
          list: [1, 2, 5, 20, 60],
          unit: this.$t("individual.label_data_monitor_unit_seconds"),
          initIndex: 0,
        },
        LINE: {
          tab: this.$t("individual.tab_data_monitor_line_config"),
          itemLabel: this.$t("individual.label_data_monitor_item"),
          thicknessLabel: this.$t("individual.label_data_monitor_line_thickness"),
          colorLabel: this.$t("individual.label_data_monitor_line_color"),
          styleLabel: this.$t("individual.label_data_monitor_line_style"),
          previewLabel: this.$t("individual.label_data_monitor_line_preview"),
        },
        COMMON: {
          OK: this.$t("message.common_button_ok"),
          CANCEL: this.$t("message.common_button_cancel"),
        },
      };
    },

    /**
     * グラフ設定のボタン表示
     * @param {*} no ボタン位置
     * @returns ボタン文字列
     */
    configGraphButtonCapt(no) {
      const dispParts = this.getDisplayPartsConfigureGraph();
      return (no === 0) ? dispParts.COMMON.CANCEL : dispParts.COMMON.OK;
    },

    /**
     * グラフ設定のOKボタン押下時の処理
     */
    async handleOkConfigGraphModal() {
      try {
        this.processing = true;
        const ret = this.$refs.config_graph.handleOkButtonClick();
        if (!ret) {
          // 入力チェックNGの場合、処理を中断
          return;
        }

        if (this.systemId === null) {
          // OBDの場合はStartからデータモニタを表示すると、システムIDが取得できないためDBに保存しない
          return;
        }

        // グラフ設定をDynamoDBに保存する
        const yRangeInfo = Object.keys(this.yRange).map((did) => {
          const response = {
            'id': did,
            'display_type': this.yRange[did].selectedRange,
          };
          if (response.display_type === RANGE.MANUAL) {
            response.min = this.yRange[did].min;
            response.max = this.yRange[did].max;
          }
          return response;
        });
        const lineSetting = [];
        Object.keys(this.lineSetting).map((did) => {
          lineSetting.push({
            'id': did,
            'color': this.lineSetting[did].colorChanged ? this.lineSetting[did].color : "",
            'style': this.lineSetting[did].style,
            'thickness': this.lineSetting[did].thickness
          });
        });
        const postBody = {
          'type': 'graph_setting',
          'data': {
            'x_settings': this.xRange,
            'y_settings': yRangeInfo,
            'line_settings': lineSetting
          },
        };
        if (this.operationScreen === config.EXECUE_FUNCTION_TYPE_DATAPLAYBACK) {
          postBody.region_info = this.projectDataSource;
        }
        await apiService.postCall(apiService.OBD, `/diagnosticSystems/${this.systemId}/measureInfo`, postBody, this.operationScreen);

        if (!this.inExecution && !this.inPlaying) {
          for (const tab of [TAB.SPLIT, TAB.UNION]) {
            // グラフ設定更新後にズーム状態をリセットしてY軸のスケール計算結果をラベルに反映する。
            this.graphMediator[tab]?.resetZoom?.();
          }
        }
      } catch (e) {
        logWrapper.log(e);
        alert.showError(this, null, this.$t('individual.error_communication_error'));
      } finally {
        this.processing = false;
        // ダイアログを閉じる
        this.modalConfigGraph = false;
      }
    },

    /**
     * グラフ設定処理
     * @param {*} userSettingInfo
     *   xRange: x軸設定情報
     *   yRange: y軸設定情報
     */
    handleConfigGraphComplete(userSettingInfo) {
      // グラフ設定(Y軸)
      if (!this.yRange) {
        this.yRange = {};
      }
      this.yRange = JSON.parse(JSON.stringify(userSettingInfo.yRange));
      this.lineSetting = this.mergeLineSetting(this.lineSetting, userSettingInfo.lineSetting);

      const settingInfo = {};
      const deleteInfo = {};
      Object.keys(this.yRange).forEach((did) => {
        const funcValue = this.funcValues[did];
        if (this.yRange[did].selectedRange === RANGE.AUTO) {
          this.YLabelUpdatedItems[did] = false;
        }
        // AUTOレンジの場合かつ現在の最大と最小が一致する場合、chart.jsにまかせるため、設定を削除する
        if (this.yRange[did].selectedRange === RANGE.AUTO && (funcValue && funcValue.min === funcValue.max)) {
          deleteInfo[did] = this.yRange[did];
        } else {
          const conversionInfo = this.conversionList[did]?.conversion;
          const digit = conversionInfo ? conversionInfo.digit_after_decimal : 0;
          const minDisp = fixNumberWithDecimal(this.yRange[did].min, digit);
          const maxDisp = fixNumberWithDecimal(this.yRange[did].max, digit);
          settingInfo[did] = this.yRange[did];
          settingInfo[did].minDisp = minDisp;
          settingInfo[did].maxDisp = maxDisp;
        }
      });

      // 分割グラフ設定
      const splitElementList = this.graphMediator[TAB.SPLIT].getGraphElementList();
      this.selectedGraphCheckList.filter((x) => x.checkValue[0]).forEach((x) => {
        const did = x.data_monitor_id;
        const splitGraphElement = splitElementList.find(element => (element._yAxesInfo[0] && element._yAxesInfo[0].id) === did);
        const yAxisInfo = splitGraphElement && splitGraphElement._yAxesInfo.find(yAxis => yAxis.id === did);

        if (did in deleteInfo) {
          this.graphMediator[TAB.SPLIT].clearMinMaxSettings(did);
        } else {
          yAxisInfo && this.graphMediator[TAB.SPLIT].yRangeSetting(splitGraphElement.id, settingInfo);
        }

        if (this.lineSetting[did] && Object.keys(this.lineSetting[did]).length > 0) {
          let color = this.lineSetting[did].color;
          if (this.lineSetting[did].borrowColor && this.lineSetting[did].borrowColor[TAB.SPLIT]) {
            color = this.lineSetting[did].borrowColor[TAB.SPLIT];
          }
          const width = this.getBorderWidthNumber(this.lineSetting[did].thickness);
          const style = this.getStyleNumber(this.lineSetting[did].style);

          yAxisInfo && this.graphMediator[TAB.SPLIT].updateGraphLineSetting(splitGraphElement.id, color, width, style);
          yAxisInfo && this.graphMediator[TAB.SPLIT].updateYLabelColor(splitGraphElement.id, color);

          if (this.lineSetting[did].colorChanged) {
            this.colorManager.registerColor(TAB.SPLIT, did, color);
          }
        }
      });

      // 結合グラフ設定
      Object.keys(deleteInfo).forEach((did) => {
        this.graphMediator[TAB.UNION].clearMinMaxSettings(did);
      });
      const id = this.unionGraphId();
      this.graphMediator[TAB.UNION].yRangeSetting(id, settingInfo);

      // グラフ設定(ライン設定)
      const unionElementList = this.graphMediator[TAB.UNION].getGraphElementList();
      const unionDidList = unionElementList[0].datasetList.map((dataset) => dataset.label);
      const unionGraphElement = unionElementList[0];

      for (const did of unionDidList) {
        let color = this.lineSetting[did].color;
        if (this.lineSetting[did].borrowColor && this.lineSetting[did].borrowColor[TAB.UNION]) {
          color = this.lineSetting[did].borrowColor[TAB.UNION];
        }
        const width = this.getBorderWidthNumber(this.lineSetting[did].thickness);
        const style = this.getStyleNumber(this.lineSetting[did].style);

        const yAxisInfo = unionGraphElement && unionGraphElement._yAxesInfo.find(yAxis => yAxis.id === did);
        yAxisInfo && this.graphMediator[TAB.UNION].updateGraphLineSetting(unionGraphElement.id, color, width, style, yAxisInfo.id);
        yAxisInfo && this.graphMediator[TAB.UNION].updateYLabelColor(unionGraphElement.id, color, yAxisInfo.lastIndex);

        if (this.lineSetting[did].colorChanged) {
          this.colorManager.registerColor(TAB.UNION, did, color);
        }
      }

      // グラフ設定(X軸)
      this.xRange = userSettingInfo.xRange;
      this.graphMediator[TAB.SPLIT].xRangeSetting(this.xRange);
      this.graphMediator[TAB.UNION].xRangeSetting(this.xRange);

      // 凡例を更新
      this.graphMediator[TAB.SPLIT].graphElementList.forEach((g) => {
        g.drawCursorLegend();
      });
      this.graphMediator[TAB.UNION].graphElementList.forEach((g) => {
        g.drawCursorLegend();
      });

      this.$emit("set-graph-config", this.xRange, this.yRange, this.lineSetting);
    },

    /**
     * 追加・削除するグラフ線のIDを取得する
     * @param {int} type グラフタイプ
     * @param {boolean} isSelect 選択されたか解除されたか
     * @returns {string} グラフID
     */
    getGraphId(type, isSelect, index) {
      if (type === dmUtil.GRAPH_CHECK_COLUMN.SPLIT) {
        // 分割グラフのindexを特定する
        const elementList = this.graphMediator[TAB.SPLIT].getGraphElementList();

        if (isSelect) {
          // 分割グラフの空きIndexを取得する
          let freeIndex = null;
          elementList.some((x, i) => {
            // _yAxesInfoが削除済みであれば空き
            const rc = (x._yAxesInfo.length === 0);
            if (rc) {
              freeIndex = i;
            }
            return rc;
          });
          // 空きIndexがあればその値、なければ最大値
          return this.splitGraphId((freeIndex !== null) ? (freeIndex + 1) : elementList.length);

        } else {
          // 分割グラフの対象indexを取得する
          const did = this.selectedGraphCheckList[index].data_monitor_id;
          let targetIndex = null;
          elementList.some((x, i) => {
            // _yAxesInfoがのidがdidと一致しているものを対象indexとする
            const rc = (x._yAxesInfo.length !== 0) && (x._yAxesInfo[0].id === did);
            if (rc) {
              targetIndex = i;
            }
            return rc;
          });
          return this.splitGraphId(targetIndex + 1);
        }

      } else {
        return this.unionGraphId();
      }
    },

    /**
     * グラフ項目変更時処理
     * @param {*} type 分割G/結合G
     * @param {*} isSelect チェック有無
     * @param {*} index 一覧のindex
     */
    async changeCheckValue(type, isSelect, index) {
      this.processing = true;
      try {
        const self = this;
        self.selectedGraphCheckList[index].checkValue[type] = isSelect;

        // タブdisable設定
        dmUtil.updateSelectedGraphLinesCount(type, isSelect, self.selectedGraphLines, { SPLIT: SPLIT_GRAPH_MAX_LINES, UNION: UNION_GRAPH_MAX_LINES });

        // ボタンdisable設定
        const checkLines = self.selectedGraphCheckList.filter((item) => (item.checkValue[0] || item.checkValue[1])).length;
        self.$emit("change-check-value", checkLines);

        // グラフ描画
        const tabId = type === dmUtil.GRAPH_CHECK_COLUMN.SPLIT ? TAB.SPLIT : TAB.UNION;
        const id = this.getGraphId(type, isSelect, index);

        const itemId = self.selectedGraphCheckList[index].data_monitor_id;

        if (isSelect) {
          // グラフ項目追加
          const itemName = self.selectedGraphCheckList[index].menu_title;
          let dataPoint = [[], [], []]; // UNIX時間、計測値、表示用の値
          const conversion = util.isNullOrDefault(self.conversionList[itemId].conversion);
          const unit = conversion !== null ? self.$t(`obd_unit.` + self.conversionList[itemId].conversion.unit) : '-';

          const yAxesVal = self.getYAxesMinMaxValue(itemId, true);
          const min = yAxesVal.min;
          const max = yAxesVal.max;

          const startTime = self.startTimeVal();

          // 停止中の場合、計測済みデータを作成する
          if (!self.inProgress()) {
            dataPoint = self.genMeasurementGraphForAll(tabId, itemId);
          }

          // ライン設定があれば反映させる
          const [color, style, width] = this.setLineSetting(tabId, itemId);
          const dataType = this.conversionList[itemId].data_type;
          self.graphMediator[tabId].addNewGraphLine(
            id, itemId, itemName, dataPoint, unit, min, max, yAxesVal.minDisp, yAxesVal.maxDisp, startTime, color, dataType, style, width);

          if (self.systemId) {
            // バックエンドからグラフ設定を取得する
            const settingHistory = await self.getGraphSetting(self.systemId, itemId);
            if (Object.keys(settingHistory).length > 0 && settingHistory.xRange) {
              // バックエンドのグラフ設定を適用する
              self.handleConfigGraphComplete(settingHistory);
            }
          }

          this.resizeAndRedraw(this.currentTab);
          await self.$nextTick();
          if (!self.inProgress()) {
            // 停止中にグラフ表示項目を追加する際は、DOM更新後にズームリセットしてY軸スケールの計算結果をラベルに反映する。
            self.graphMediator?.[TAB.SPLIT]?.resetZoom?.();
            self.graphMediator?.[TAB.UNION]?.resetZoom?.();
          }
        } else {
          // グラフ削除時、その項目の最終描画時刻もクリアする
          if (self.graphRanges[String(tabId)]) {
            self.graphRanges[String(tabId)][itemId] = {};
          }
          // グラフ項目削除
          self.graphMediator[tabId].deleteGraphLine(id, itemId);
          // 使用済みの色を返却
          this.colorManager.returnColor(tabId, itemId);
          // ライン設定に使用済みの色を保持している場合は削除
          if (this.lineSetting[itemId].borrowColor && this.lineSetting[itemId].borrowColor[tabId]) {
            delete this.lineSetting[itemId].borrowColor[tabId];
          }
          // カラーマネージャーで管理しているユーザー設定の色を削除
          this.colorManager.removeColor(tabId, itemId);
          // チェックを外すとmin/maxの幅が変わるため、グラフの再描画を実施
          this.resizeAndRedraw(this.currentTab);
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.processing = false;
      }
    },

    /**
     * グラフ追加時に全グラフデータ作成
     * @returns グラフライブラリに渡すデータリスト[[UNIX時間, ...], [値, ...], [表示値, ...]]
     */
    genMeasurementGraphForAll(tab, id) {
      const self = this;
      const resultList = [[], [], []];
      if (!self.convertedData || !Object.keys(self.convertedData).length) {
        return resultList;
      }

      if (!(String(tab) in self.graphRanges)) {
        self.graphRanges[String(tab)] = {};
      }

      if (!(id in self.graphRanges[String(tab)])) {
        self.graphRanges[String(tab)][id] = {};
      }

      // グラフに追加するデータを作成
      self.convertedData[id] && self.convertedData[id].forEach((data) => {
        // 時刻
        resultList[0].push(data.time);

        // データポイント
        const dataPointValue = this.convertDataPointValue(id, data.convertedData);
        resultList[1].push(dataPointValue[0]);
        resultList[2].push(dataPointValue[1]);
      });

      const latest = self.convertedData[id].length ? self.convertedData[id][self.convertedData[id].length - 1].time : 0;
      const first = self.convertedData[id].length ? self.convertedData[id][0].time : 0;
      self.graphRanges[String(tab)][id].drawLastTime = latest;
      self.graphRanges[String(tab)][id].drawFirstTime = first;

      return resultList;
    },

    /**
     * マーカ情報の保存
     */
    async markUpdate() {
      const isUpdate = this.showDeleteButton;

      let res = null;
      // 更新時
      if (isUpdate) {
        res = await this.$refs.modal_mark.update();
        // 登録時
      } else {
        res = await this.$refs.modal_mark.save();
      }

      // タイムライン上のマーカ情報を更新
      if (res && res.uuid) {
        this.markerUuid = res.uuid;
        this.setMarker(res.time + this.baseTime, res.comment, res.uuid, res.tag, isUpdate);
      }

      if (res && res.error) {
        // 実行中の場合
        if (this.inExecution) {
          await this.$emit("stop-monitor");
        }
        this.$emit("show-disconnected-server-error", ERR_MSG_KEY.FETCH, false);
      }
      if (res) {
        this.closeMarkModal();
      }
    },

    /**
     * マーカ情報の削除
     */
    async markDelete() {
      const res = await this.$refs.modal_mark.clear();
      if (!res.error) {
        // Intdashからの削除に成功したら、画面上のマーカーも消す
        this.deleteMarker();
      }
      this.markerUuid = "";
      this.closeMarkModal();
    },

    /**
     * マーカダイアログを非表示にする
     */
    closeMarkModal() {
      this.modalMark = false;
    },

    /**
     * マーカダイアログを表示する（新規登録時）
     */
    async openMarkModalForNew(tab) {
      // 自動保存中の場合は処理を中止する
      if (this.isAutoMarkSaving) {
        return;
      }

      // 自動保存中の場合
      if (this.inExecution) {
        this.isAutoMarkSaving = true;
      }

      this.markerTargetTab = tab;
      this.showDeleteButton = false;
      this.elapsedTime = Math.floor(this.graphCurrentTimes[this.currentTab]) - this.baseTime;
      this.markerUuid = "";

      // HACK: 後段で呼ぶ子コンポーネント(MarkPage)のメソッドにプロパティの更新が間に合わないため、プロパティを強制的に更新する。
      //       DataMonitorMeasure内で処理を完結できれば、プロパティの更新を気にする必要はないのではないか。
      await this.loadMarkIncrement();

      // 計測中の場合はダイアログを表示せずにマーカ情報を登録する
      if (this.inExecution) {
        await this.markUpdate();
        this.isAutoMarkSaving = false;
      }
      else {
        //ダイアログを表示する
        this.modalMark = true;
      }
    },

    /**
     * マーカダイアログを表示する（更新時）
     */
    async openMarkModalForUpdate(event, tabId) {
      this.markerTargetTab = tabId;
      this.eventDelete = event;
      this.showDeleteButton = true;

      // マーカ情報読込み
      this.readMarker(event, tabId);
      this.markerUuid = this.markerInfo.markerUuid;

      // HACK: 後段で呼ぶ子コンポーネント(MarkPage)のメソッドにプロパティの更新が間に合わないため、プロパティを強制的に更新する。
      //       DataMonitorMeasure内で処理を完結できれば、プロパティの更新を気にする必要はないのではないか。
      await this.loadMarkIncrement();
      this.modalMark = true;
    },

    /**
     * プロパティ更新用関数
     */
    async loadMarkIncrement() {
      this.loadMark++;
    },

    /**
     * マーカ設定
     * @param {number} time 基準時刻+経過時間
     * @param {string} comment コメント
     * @param {string} uuid マーカUUID
     * @param {object} tag タグ情報
     */
    setMarker(time, comment, uuid, tag) {
      const self = this;
      // マーカーを登録したタブ以外でもマーカーを登録
      for (const tabId of Object.values(TAB)) {
        if (!this.graphMediator[tabId]) {
          continue;
        }

        // マーカ情報の更新
        self.graphMediator[tabId].setMarker(time, comment, uuid, tag);

        // ダブルクリック時のダイアログオープン
        const markerElement = self.graphMediator[tabId].markerElement;
        if (markerElement) {
          markerElement.addEventListener('dblclick', (event) => {
            self.openMarkModalForUpdate(event, tabId);
          }, self);

          // マウスover時のマーカ情報読込
          markerElement.addEventListener('mouseover', (event) => {
            self.readMarker(event, tabId);
          }, self);

          self.markerElementArray[tabId].push(markerElement);
        }
      }
    },

    /**
     * マーカー削除
     */
    deleteMarker() {
      // 各タブのマーカー削除
      for (const tabId of Object.values(TAB)) {
        if (!this.graphMediator[tabId]) {
          continue;
        }
        this.graphMediator[tabId].deleteMarkerByUuid(this.markerInfo.markerUuid);
      }
    },

    /**
     * マーカー情報リード
     */
    readMarker(markerElement, tabId) {
      this.markerInfo = this.graphMediator[tabId].readMarker(markerElement);
    },

    /**
     * カーソル追加
     * @param {string} cursorType カーソル種別
     */
    setCursor(cursorType) {
      const currentTime = Math.floor(this.graphCurrentTimes[this.currentTab]);
      this.cursor[cursorType] = currentTime;

      for (const tabId of Object.values(TAB)) {
        if (!this.graphMediator[tabId] || tabId === TAB.LIST) {
          // リストはカーソル表示しない
          continue;
        }

        // カーソル情報の更新
        this.graphMediator[tabId].setCursor(cursorType, currentTime);
        this.graphMediator[tabId].updateComponent();
      }
    },

    /**
     * カーソル表示中か否かを返す
     * @returns カーソル表示中か否か
     */
    isCursorDisplay() {
      return Object.keys(this.cursor).every(key => this.cursor[key] !== null);
    },

    /**
     * カーソルをタブ間で同期する
     * @param {string} cursorType カーソル種別
     * @param {number} time カーソルを追加する時間
     */
    syncCursor(cursorType, time) {
      for (const tabId of Object.values(TAB)) {
        if (!this.graphMediator[tabId] || tabId === TAB.LIST || tabId === this.currentTab) {
          // リストはカーソル表示しない
          continue;
        }

        // カーソル情報の更新
        this.graphMediator[tabId].setCursor(cursorType, time);
        this.graphMediator[tabId].updateComponent();
      }
    },

    /**
     * グラフのステップ設定を行う
     */
    stepSetting() {
      const setStepSmall = document.getElementById("stepSmall");
      const setStepLarge = document.getElementById("stepLarge");
      this.graphMediator[TAB.SPLIT].stepSetting(parseInt(setStepSmall.value, 10), parseInt(setStepLarge.value, 10));
    },

    /**
     * グラフ初期設定
     * @param {*} tabId
     */
    setGraph(tabId) {
      linkSplitUnionMediators(this.graphMediator);

      if (tabId === TAB.LIST) {
        if (!this.graphMediator[tabId]) {
          this.graphMediator[tabId] = new GraphMediator();
          this.graphMediator[tabId].setTimelineElement(this.listTimeLineId());
          this.$refs.timeLineSeekBar._handleChange = this.listTimelineSeek;
          if (!this.inExecution) {
            this.graphMediator[tabId].enableTimeline();
          }
          this.graphMediator[tabId].setDataAppendingStatus(true);
          this.addButtonEventListener(tabId);
          return;
        }
      }

      // マーカ登録/読み込み/削除
      this.graphMediator[tabId].validation();

      if (tabId === TAB.UNION) {
        // 結合グラフはXとY両方のズーム・パンを可能とする
        this.graphMediator[tabId].setZoomPanMode(DEFAULT_ZOOM_PAN_MODE);
        this.graphMediator[tabId].setGraphElementList([this.unionGraphId()], this.syncCursor);
        this.graphMediator[tabId].setTimelineElement(this.unionTimeLineId());
        this.graphMediator[tabId].setDataAppendingStatus(true);
        this.addButtonEventListener(tabId);
        // xRangeがあれば設定する
        if (this.graphConfigInfo && this.graphConfigInfo.xRange) {
          this.graphMediator[tabId].xRangeSetting(this.graphConfigInfo.xRange);
        }
      } else if (tabId === TAB.SPLIT) {
        // 分割グラフはX軸のみのズーム・パンを可能とする
        this.graphMediator[tabId].setZoomPanMode(ZOOM_PAN_MODE_X);
        const ids = [];
        for (let num = 0; num < this.selectedFunctionKeys().length; num++) {
          const id = this.splitGraphId(num + 1);
          ids.push(id);
        }
        this.graphMediator[tabId].setGraphElementList(ids, this.syncCursor);
        this.graphMediator[tabId].setTimelineElement(this.splitTimeLineId());
        this.graphMediator[tabId].setDataAppendingStatus(true);
        this.addButtonEventListener(tabId);
        // xRangeがあれば設定する
        if (this.graphConfigInfo && this.graphConfigInfo.xRange) {
          this.graphMediator[tabId].xRangeSetting(this.graphConfigInfo.xRange);
        }
      }

      // 翻訳文字列をグラフに設定
      this.graphMediator[tabId].setTranslatedValue(
        this.$t("individual.header_data_monitor_table_max"),
        this.$t("individual.header_data_monitor_table_min"),
        this.$t("individual.header_data_monitor_table_avg"),
        this.$t("individual.header_data_monitor_table_value")
      );

      // 計測・再生状態によってズーム機能の有効・無効を切り換える
      dmUtil.setGraphZoomEnabled(this.graphMediator, !this.inExecution && !this.inPlaying);
    },

    /**
     * ボタンにイベントを登録する
     * @param {*} tabId
     */
    addButtonEventListener(tabId) {
      const elementHashArray = {};
      // [<<]ボタン
      const stepBackMaxElement = document.getElementById(`${this.componentName()}-dm-backLong-` + tabId);
      if (stepBackMaxElement) {
        elementHashArray.stepBackMax = stepBackMaxElement;
      }
      // [<]ボタン
      const stepBackMinElement = document.getElementById(`${this.componentName()}-dm-backShort-` + tabId);
      if (stepBackMinElement) {
        elementHashArray.stepBackMin = stepBackMinElement;
      }
      // [>]ボタン
      const stepMinElement = document.getElementById(`${this.componentName()}-dm-forwardShort-` + tabId);
      if (stepMinElement) {
        elementHashArray.stepFwdMin = stepMinElement;
      }
      // [>>]ボタン
      const stepMaxElement = document.getElementById(`${this.componentName()}-dm-forwardLong-` + tabId);
      if (stepMaxElement) {
        elementHashArray.stepFwdMax = stepMaxElement;
      }

      this.graphMediator[tabId].addEventListener(elementHashArray);
    },

    /**
     * グラフX軸情報の設定
     * @returns
     */
    setXAxesInfo() {
      return JSON.stringify({
        startTime: this.startTimeVal(),
        range: this.endTimeVal() - this.startTimeVal(),
      });
    },

    /**
     * グラフY軸情報の設定
     * @param {*} tabId
     * @param {*} index
     * @returns
     */
    setYAxesInfo(tabId, index = 0) {
      const self = this;
      const val = [];

      const colorInfo = this.colorManager && this.colorManager.getUsingColors(tabId);

      if (self.graphMediator[tabId]) {
        const elementList = self.graphMediator[tabId].getGraphElementList();

        elementList[index]._yAxesInfo.forEach((item) => {
          const did = item.id;
          if (!self.conversionList[did]) {
            val.push(item);
            return;
          }

          const isValidConversion = util.isNullOrDefault(self.conversionList[did].conversion) !== null;
          const yAxesVal = self.getYAxesMinMaxValue(did);
          const min = yAxesVal.min;
          const max = yAxesVal.max;

          // 線のスタイル、太さを取得
          const style = this.lineSetting[did] ? this.getStyleNumber(this.lineSetting[did].style) : STYLE.SOLID;
          const width = this.lineSetting[did] ? this.getBorderWidthNumber(this.lineSetting[did].thickness) : GRAPH_LINE_NORMAL;

          const graphInfo = {
            itemName: item.itemName,
            // conversionListの値を反映
            unit: isValidConversion ? self.$t(`obd_unit.` + self.conversionList[did].conversion.unit) : '-',
            id: did,
            max: max,
            min: min,
            maxDisp: yAxesVal.maxDisp,
            minDisp: yAxesVal.minDisp,
            // 小数点以下の桁数。ズームやパンでmin/maxを更新する際に参照する。
            digitAfterDecimal: isValidConversion ? this.conversionList[did].conversion.digit_after_decimal : 0,
            lastIndex: item.lastIndex,
            color: colorInfo ? colorInfo[did] : dmUtil.ColorManager.DEFAULT_COLOR,
            style: style,
            width: width,
            dataType: this.conversionList[did].data_type,
          };

          val.push(graphInfo);
        });
      }

      if (val.length > 0) {
        // _yAxesInfoが存在する場合は、それを利用する
        return JSON.stringify(val);
      }

      // チェックされた状態で_yAxesInfoを設定する場合
      // 結合タブ
      if (tabId === TAB.UNION) {
        let targetList = [];
        if (self.selectedGraphCheckList.length > 0) {
          // 変更後のグラフ項目を対象とする
          targetList = self.selectedGraphCheckList.filter((x) => x.checkValue[1]);
        }
        targetList.forEach((item, ind) => {
          const id = item.data_monitor_id;
          if (!self.conversionList[id]) {
            return;
          }

          const isValidConversion = util.isNullOrDefault(self.conversionList[id].conversion) !== null;
          const yAxesVal = self.getYAxesMinMaxValue(id);
          const min = yAxesVal.min;
          const max = yAxesVal.max;

          // 線のスタイル、太さを取得
          const style = this.lineSetting[id] ? this.getStyleNumber(this.lineSetting[id].style) : STYLE.SOLID;
          const width = this.lineSetting[id] ? this.getBorderWidthNumber(this.lineSetting[id].thickness) : GRAPH_LINE_NORMAL;

          const graphInfo = {
            itemName: item.menu_title,
            unit: isValidConversion ? self.$t(`obd_unit.` + self.conversionList[id].conversion.unit) : '-',
            id: id,
            max: max,
            min: min,
            maxDisp: yAxesVal.maxDisp,
            minDisp: yAxesVal.minDisp,
            // 小数点以下の桁数。ズームやパンでmin/maxを更新する際に参照する。
            digitAfterDecimal: isValidConversion ? this.conversionList[id].conversion.digit_after_decimal : 0,
            lastIndex: ind,
            color: colorInfo ? colorInfo[id] : dmUtil.ColorManager.DEFAULT_COLOR,
            style: style,
            width: width,
            dataType: this.conversionList[id].data_type,
          };
          val.push(graphInfo);
        });

      }
      // 分割タブ
      else if (tabId === TAB.SPLIT) {
        let targetList = [];
        if (self.selectedGraphCheckList.length > 0) {
          // 変更後のグラフ項目を対象とする
          targetList = self.selectedGraphCheckList.filter((x) => x.checkValue[0]);
        }
        let did = targetList[index] ? targetList[index].data_monitor_id : "";
        if (this.graphMediator[TAB.SPLIT]) {
          // グラフ項目が変更された場合は変更後のindexのdidを対象とする
          const elementList = this.graphMediator[TAB.SPLIT].getGraphElementList();
          if (elementList.length > index) {
            if (elementList[index]._yAxesInfo.length === 0) {
              // 削除済みのindexのため、無視
              return JSON.stringify(val);
            }
            did = elementList[index]._yAxesInfo[0].id;
          }
        }

        if (self.conversionList[did] === undefined) {
          return JSON.stringify(val);
        }

        const isValidConversion = util.isNullOrDefault(self.conversionList[did].conversion) !== null;
        const yAxesVal = self.getYAxesMinMaxValue(did);
        const min = yAxesVal.min;
        const max = yAxesVal.max;

        // 線のスタイル、太さを取得
        const style = this.lineSetting[did] ? this.getStyleNumber(this.lineSetting[did].style) : STYLE.SOLID;
        const width = this.lineSetting[did] ? this.getBorderWidthNumber(this.lineSetting[did].thickness) : GRAPH_LINE_NORMAL;

        const graphInfo = {
          itemName: targetList[index].menu_title,
          unit: isValidConversion ? self.$t(`obd_unit.` + self.conversionList[did].conversion.unit) : '-',
          id: did,
          max: max,
          min: min,
          maxDisp: yAxesVal.maxDisp,
          minDisp: yAxesVal.minDisp,
          // 小数点以下の桁数。ズームやパンでmin/maxを更新する際に参照する。
          digitAfterDecimal: isValidConversion ? this.conversionList[did].conversion.digit_after_decimal : 0,
          lastIndex: 0,
          color: colorInfo ? colorInfo[did] : dmUtil.ColorManager.DEFAULT_COLOR,
          style: style,
          width: width,
          dataType: this.conversionList[did].data_type,
        };
        val.push(graphInfo);
      }

      return JSON.stringify(val);
    },

    /**
     * シークバーへ設定する開始時刻を取得
     * @returns 開始時刻
     */
    startTimeVal() {
      return this.baseTime;
    },

    /**
     * シークバーへ設定する終了時刻を取得
     * @returns 終了時刻
     */
    endTimeVal() {
      if (this.baseTime === null) {
        return null;
      }

      const period = (this.latestOffsetTime === 0) ? 1 * 60 * 1000 : this.latestOffsetTime;
      return this.baseTime + period;
    },

    /**
     * 結合グラフカスタムエレメント（グラフ）に設定するIDを取得
     * @returns ID文字列
     */
    unionGraphId() {
      return GRAPH.UNION;
    },

    /**
     * 分割グラフカスタムエレメント（グラフ）に設定するIDを取得
     * @param {*} グラフ番号
     * @returns ID文字列
     */
    splitGraphId(num) {
      return GRAPH.SPLIT.concat("0".repeat(2).concat(String(num)).slice(-3));
    },

    /**
     * 結合グラフカスタムエレメント（タイムライン）に設定するIDを取得
     * @returns ID文字列
     */
    unionTimeLineId() {
      return TIMELINE.UNION;
    },

    /**
     * 分割グラフカスタムエレメント（タイムライン）に設定するIDを取得
     * @returns ID文字列
     */
    splitTimeLineId() {
      return TIMELINE.SPLIT;
    },

    /**
     * リストカスタムエレメント（タイムライン）に設定するIDを取得
     * @returns ID文字列
     */
    listTimeLineId() {
      return TIMELINE.LIST;
    },

    /**
     * コンポーネント名を取得
     * ただし画面ごとに被るため、呼び出し元画面のIDを付与する。
     * @returns {string} コンポーネント名 + 呼び出し元画面ID
     */
    componentName() {
      const typeCaller = this.dataMonitorFunctionSettings.getTypeCaller();
      return `${COMPONENT_NAME}_${typeCaller}`;
    },

    /**
     * 分割／結合グラフの表示数制限した選択項目リストの取得
     * @param {*} tab
     * @returns 表示数制限した選択項目リスト
     */
    selectedFunctionKeys(tab = TAB.SPLIT) {
      return Object.keys(this.selectedFunctionList).slice(0, (tab === TAB.SPLIT) ? SPLIT_GRAPH_MAX_LINES : UNION_GRAPH_MAX_LINES);
    },

    /**
     * 分割グラフで表示対象かを確認
     * @param {*} index
     * @returns 表示対象
     */
    isSelectedFunctionKey(index) {
      // チェック変更のイベントで動くように、selectedGraphLinesの変数でも判定
      if (this.graphMediator[TAB.SPLIT] && this.selectedGraphLines[TAB.SPLIT]) {
        const elementList = this.graphMediator[TAB.SPLIT].getGraphElementList();
        if (elementList.length > index) {
          if (elementList[index]._yAxesInfo.length === 0) {
            // 存在しないindexのため、無視
            return false;
          }
          return true;
        }
      }
      return false;
    },

    showUnionGraph() {
      return this.selectedGraphLines[TAB.UNION];
    },

    /**
     * グラフエリアのstyleを取得
     * @param {*} index
     * @returns style
     */
    getGraphAreaStyle(index) {
      let style = '';
      if (!this.isSelectedFunctionKey(index)) {
        // 非表示の設定
        style = "display: none;";
      }
      return style;
    },

    /**
     * リスト表示用のタイムラインシークバー摘み位置更新時のコールバック
     * @param {number} currentMs
     */
    listTimelineSeek(currentMs) {
      this.handleTimelineSeek(currentMs);
      // リストのカレント移動時、グラフの範囲をクリアし、グラフタブのカレント位置を同期させる
      [TAB.SPLIT, TAB.UNION].forEach(tabId => {
        this.graphMediator[tabId].updateGraphRange(this.startTimeVal(), this.endTimeVal());
        this.graphMediator[tabId].timelineElement.setRangeSliderPosition(this.startTimeVal(), this.endTimeVal());
        this.graphMediator[tabId].seekToTime(currentMs, false, false);
      });
    },

    /**
     * グラフタイムラインシークバー摘み位置更新時のコールバック
     * @param {number} currentMs
     */
    graphTimelineSeek(currentMs) {
      this.handleTimelineSeek(currentMs);
    },

    handleTimelineSeek(currentMs) {
      const items = {};
      for (const func of this.selectedFunctionList) {
        // 計測済データが存在しない場合は処理をスキップする
        if (!this.convertedData[func.data_monitor_id]) {
          continue;
        }

        // データポイントの中で一番時間が近いものを探す
        const times = this.convertedData[func.data_monitor_id].map(item => item.time);
        const targetIndex = binarySearch(times, currentMs);
        if (targetIndex >= 0 && targetIndex <= times.length) {
          items[func.data_monitor_id] = this.convertedData[func.data_monitor_id][targetIndex];
        }
      }
      // リストの値更新
      this.executionList = items;

      Object.values(dmUtil.TAB).forEach(tab => {
        this.graphCurrentTimes[tab] = Math.floor(currentMs);

        // 表示中タブのマーカー更新
        this.graphMediator[this.currentTab].graphElementList.forEach((g) => {
          g.updateMarker();
          g.drawCurrentLine();
        });

        if (this.currentTab !== tab) {
          // 他タブのスライダーを同期
          this.graphMediator[tab].timelineElement.setCurrentSliderPosition(currentMs);
          this.graphMediator[tab].timelineElement.updateComponent();
        }
      });
    },

    /**
     * グラフタイムラインシークバーの開始・終了摘み位置更新時のコールバック
     * @param {number} newStartTimeMs
     * @param {number} newEndTimeMs
     */
    graphTimelineRangeChange(newStartTimeMs, newEndTimeMs) {
      // シークバー操作でX軸の表示倍率が変わるため、倍率変更プルダウンの表示を-%に戻す
      this.zoomDisplayValue = DEFAULT_ZOOM_DISPLAY_VALUE;

      Object.values(dmUtil.TAB).forEach(tab => {
        if (this.currentTab !== tab) {
          // グラフの表示範囲と他タブのスライダーを同期
          this.graphMediator[tab].updateGraphViewRange(newStartTimeMs, newEndTimeMs);
          this.graphMediator[tab].timelineElement.setRangeSliderPosition(newStartTimeMs, newEndTimeMs);
        }
      });
    },

    /**
     * 描画完了処理
     */
    completeDraw() {
      if ((this.drawTimer !== null) && (this.drawTimer !== undefined)) {
        // 描画インターバルタイマ停止
        this.drawTimer = null;
      }

      if (this.graphMediator.length > 0) {
        // タイムラインシークバー有効化
        this.graphMediator[TAB.LIST].enableTimeline();
        this.graphMediator[TAB.SPLIT].enableTimeline();
        this.graphMediator[TAB.UNION].enableTimeline();
        // チェックボックスが1つもチェックされていない場合、グラフのタイムラインは一度も更新されていないため、最新の時間で更新する
        this.graphMediator[TAB.SPLIT].timelineElement.updateComponent();
        this.graphMediator[TAB.UNION].timelineElement.updateComponent();

        // データ投入中フラグをOFF
        this.graphMediator[TAB.LIST].setDataAppendingStatus(false);
        this.graphMediator[TAB.SPLIT].setDataAppendingStatus(false);
        this.graphMediator[TAB.UNION].setDataAppendingStatus(false);

        this.graphMediator[TAB.SPLIT].resetZoom?.();
        this.graphMediator[TAB.UNION].resetZoom?.();
      }
    },

    /**
     * 計測や再生の再実行用の初期化処理
     */
    initReExecute() {
      // データをクリア
      this.clearData();

      // 言語変換対応
      this.changeDmi18nSelectedFunctionList();

      // タイムラインが未初期化の場合は以降の処理を中止する
      if (!this.graphMediator[TAB.LIST] || !this.graphMediator[TAB.LIST] || !this.graphMediator[TAB.UNION]) {
        return;
      }

      // タイムラインシークバー無効化
      this.graphMediator[TAB.LIST].disableTimeline();
      this.graphMediator[TAB.SPLIT].disableTimeline();
      this.graphMediator[TAB.UNION].disableTimeline();

      // 前回計測のマーカーを削除
      const self = this;
      self.markerElementArray.forEach((arr, tab) => {
        arr.forEach((m) => {
          try {
            self.graphMediator[tab].timelineElement._timelineComponent.removeChild(m);
          } catch (e) {
            logWrapper.log('remove marker error: ', e);
          }
        });
      });

      // 前回のカーソル削除
      this.graphMediator[TAB.SPLIT].deleteCursor();
      this.graphMediator[TAB.UNION].deleteCursor();
      this.cursor[config.CURSOR.A] = null;
      this.cursor[config.CURSOR.B] = null;

      // 前回計測データクリア
      this.graphMediator[TAB.LIST].close();
      this.graphMediator[TAB.UNION].close();
      this.graphMediator[TAB.SPLIT].close();

      // データ投入中フラグをON
      this.graphMediator[TAB.LIST].setDataAppendingStatus(true);
      this.graphMediator[TAB.UNION].setDataAppendingStatus(true);
      this.graphMediator[TAB.SPLIT].setDataAppendingStatus(true);

      // 初期化直後はズーム/パン状態ではないため、状態にfalseを設定する。
      this.$emit('zoom-pan-state-changed', false);
    },

    /**
     * タイムラインコンポーネントからマーカー情報を取得する
     * @returns マーカー情報
     */
    getMarkers() {
      return this.graphMediator[TAB.LIST].timelineElement.markerHashArray;
    },

    /** 項目選択に戻る */
    backSelect() {
      this.$emit('back-select');

      this.colorManager?.clearRegister();
    },

    /** モニタ停止 */
    stopMonitor() {
      this.$emit('stop-monitor');
    },

    /** モニタ開始 */
    startMonitor() {
      this.$emit('start-monitor');
    },

    /**
     * DBに保存したグラフ設定を取得する関数
     * @param {string} system システム名
     * @param {string} did APIのDIDに関する引数は配列なので、API実行時は配列にする
     * @returns グラフ設定情報
    */
    async getGraphSetting(system, did) {
      let response = {};
      try {
        const queryParam = this.specifiedSystemId
          ? new URLSearchParams({ 'type': 'graph_setting', 'target_pid_did_list': JSON.stringify([did]), 'region_info': `${this.projectDataSource}` }).toString()
          : new URLSearchParams({ 'type': 'graph_setting', 'target_pid_did_list': JSON.stringify([did]) }).toString();
        const apiResponse = await apiService.getCall(
          apiService.OBD,
          `/diagnosticSystems/${system}/measureInfo?${queryParam}`,
          this.operationScreen
        );
        if (apiResponse.data.x_settings) {
          response.xRange = apiResponse.data.x_settings;
        }
        if (apiResponse.data.y_settings) {
          const yRange = {};
          apiResponse.data.y_settings.forEach((did) => {
            yRange[did.id] = {
              "min": Number(did.min) || this.conversionList[did.id].conversion?.graph_range_display?.min,
              "max": Number(did.max) || this.conversionList[did.id].conversion?.graph_range_display?.max,
              "selectedRange": did.display_type
            };
          });

          // OBDではgraph_range_displayのmin/maxが両方0になるケースは今のところないが、グラフ表示できなくなるため、両方0のケースははじく
          if (
            apiResponse.data.y_settings.length === 0 &&
            (this.conversionList[did]?.conversion?.graph_range_display?.min !== 0 || this.conversionList[did]?.conversion?.graph_range_display?.max !== 0)
          ) {
            yRange[did] = {
              "min": this.conversionList[did]?.conversion?.graph_range_display?.min,
              "max": this.conversionList[did]?.conversion?.graph_range_display?.max,
              "selectedRange": RANGE.AUTO
            };
          }

          response.yRange = yRange;
        }
        if (apiResponse.data.line_settings) {
          const lineSetting = {};

          apiResponse.data.line_settings.forEach((did) => {
            lineSetting[did.id] = {
              thickness: did.thickness,
              style: did.style,
              color: did.color,
              colorChanged: did.color !== ""
            };
          });
          response.lineSetting = lineSetting;
        }
      } catch (e) {
        response = {};
        logWrapper.log(e);
        alert.showError(this, null, this.$t('individual.error_communication_error'));
      }
      return response;
    },

    /**
     * ライン設定情報マージする
     * @param {object} linsSetting ライン設定情報
     * @param {object} receivedLineSetting DBから取得したライン設定情報
     * @returns {object} マージしたライン設定情報
     */
    mergeLineSetting(linsSetting, receivedLineSetting) {
      const result = receivedLineSetting;
      Object.keys(linsSetting).forEach((did) => {
        if (!(did in result)) {
          result[did] = linsSetting[did];
        } else if (!result[did].color) {
          // 取得したライン設定情報に色が設定されていない場合は、元々のライン設定情報を優先する。
          result[did].color = linsSetting[did].color;
        }
        if (!result[did].colorChanged && linsSetting[did].borrowColor) {
          // colorManagerで管理している色を保持している場合は、resultに含める
          result[did].borrowColor = linsSetting[did].borrowColor;
        }
        result[did].colorChanged && delete result[did].borrowColor;
      });

      return result;
    },

    /**
    * 現在のタブのグラフを再描画する
    */
    refreshCurrentTabGraphs() {
      const currentGraphElements = this.graphMediator[this.currentTab]?.graphElementList || [];
      currentGraphElements.forEach((graphElement) => {
        graphElement.refreshGraph(); // refreshGraph を呼び出す
      });
    },

    /**
     * グラフをリサイズして、カレントラインやマーカー位置を調整して再描画する
     * リサイズ後の結果に線を引き直すため、停止後もリサイズを実施する
     * @param {string} tab 現在表示中のタブ
    */
    resizeAndRedraw(tab) {
      if (tab === TAB.SPLIT && this.graphMediator[TAB.SPLIT]) {
        this.resize(TAB.SPLIT);
        !this.inProgress() && this.graphMediator[TAB.SPLIT].updateComponent();
      } else if (tab === TAB.UNION && this.graphMediator[TAB.UNION]) {
        this.resize(TAB.UNION);
        !this.inProgress() && this.graphMediator[TAB.UNION].updateComponent();
      } else if (tab === TAB.LIST && this.graphMediator[TAB.LIST]) {
        // リスト画面のマーカーの画像位置を調整
        !this.inProgress() && this.graphMediator[TAB.LIST].timelineElement.updateMarker();
      }
    },

    /**
     * グラフをリサイズする
     * @param {string} tab 現在表示中のタブ
     */
    resize(tab = this.currentTab) {
      this.graphMediator[tab].resize();
    },

    /**
     * マークボタンにフォーカスする
     */
    markButtonFocus() {
      const button = this.$refs[`mark_button_${this.currentTab}`].$el;
      if (!button.disabled && this.inExecution && !this.isFocused) {
        button.focus();
        this.isFocused = true;
      }
    },

    /**
     * リスト行クリック時のコールバック処理
     * @param {number} rowNumber クリックされた行番号
     */
    onClickRow(rowNumber) {
      if (!this.selectedFunctionList[rowNumber]) {
        return;
      }
      // 結合グラフのカーソル項目を更新する
      const selectedItem = this.selectedFunctionList[rowNumber].data_monitor_id;
      const unionColor = this.colorManager?.getUsingColors(dmUtil.TAB.UNION);
      this.graphMediator[TAB.UNION].updateAllGraphCursor(selectedItem, unionColor);
    },

    /**
     * グラフ横並び表示のリストの表示を切り替える
     */
    onClickShowListWithGraph() {
      this.isShowListWithGraph = !this.isShowListWithGraph;
      // リスト表示切替後、グラフのリサイズを行う
      this.$nextTick(() => {
        this.resizeAndRedraw(this.currentTab);
      });
    },

    /**
     * 分割グラフの使用中の色を取得する
     */
    getSplitGraphItemColors() {
      return this.colorManager?.getUsingColors(dmUtil.TAB.SPLIT) || {};
    },

    /**
     * 結合グラフの使用中の色を取得する
     */
    getUnionGraphItemColors() {
      return this.colorManager?.getUsingColors(dmUtil.TAB.UNION) || {};
    },

    /**
     * 既存のライン設定を取得する
     * @param {number} tabId タブID (分割グラフ|結合グラフ)
     * @param {string} itemId 項目ID (e.g. 'ABG_25ePF\t101A_1_all')
     * @returns {array} 線の色、スタイル、太さ
     */
    setLineSetting(tabId, itemId) {
      let color = "";
      let style = [];
      let width = GRAPH_LINE_NORMAL;

      if (this.lineSetting && this.lineSetting[itemId]) {
        if (this.lineSetting[itemId].color && (this.lineSetting[itemId].colorChanged === undefined || this.lineSetting[itemId].colorChanged)) {
          color = this.hexToHsla(this.lineSetting[itemId].color);
          this.colorManager.registerColor(tabId, itemId, color);
          this.lineSetting[itemId].colorChanged = true;
        } else {
          color = this.hslaToHex(this.colorManager.borrowColor(tabId, itemId));
          this.lineSetting[itemId].color = color;
          this.lineSetting[itemId].colorChanged = false;
          if (!this.lineSetting[itemId].borrowColor) {
            this.lineSetting[itemId].borrowColor = {};
          }
          this.lineSetting[itemId].borrowColor[tabId] = color;
        }
        style = this.lineSetting[itemId].style.split(",").length > 1 ? this.lineSetting[itemId].style.split(",") : [];
        width = this.getBorderWidthNumber(this.lineSetting[itemId].thickness);
      } else {
        color = this.hslaToHex(this.colorManager.borrowColor(tabId, itemId));
        this.lineSetting[itemId] = {
          color: color,
          style: style.join(),
          thickness: NORMAL,
          colorChanged: false,
          borrowColor: {
            [tabId]: color
          }
        };
      }

      return [color, style, width];
    },

    /**
     * HEX形式のカラーコードをHSLAに変換する
     * @param {string} hex HEX形式カラーコード
     * @returns HSLA形式
     */
    hexToHsla(hex) {
      const _hex = hex.replace(/^#/, '');

      // RGBを0-1の範囲に正規化
      const red = parseInt(_hex.substring(0, 2), 16) / 255;
      const green = parseInt(_hex.substring(2, 4), 16) / 255;
      const blue = parseInt(_hex.substring(4, 6), 16) / 255;

      const max = Math.max(red, green, blue);
      const min = Math.min(red, green, blue);
      const lightness = (max + min) / 2;
      let hue, saturation;

      if (max === min) {
        // 最大値と最小値が等しい場合（グレースケールの場合）、色相と彩度は0になる
        hue = 0;
        saturation = 0;
      } else {
        const diff = max - min;

        // 色相の計算
        switch (max) {
          case red: hue = (green - blue) / diff + (green < blue ? 6 : 0); break;
          case green: hue = (blue - red) / diff + 2; break;
          case blue: hue = (red - green) / diff + 4; break;
          default: break;
        }
        // 0-1の範囲に正規化
        hue /= 6;

        // 彩度の計算
        saturation = lightness > 0.5 ?
          diff / (2 - (max + min)) :
          diff / (max + min);
      }

      // HSLAの値を0-360, 0-100, 0-100, 0-1の範囲に変換
      return `hsla(${Math.round(hue * 360)}, ${Math.round(saturation * 100)}%, ${Math.round(lightness * 100)}%, 1)`;
    },

    /**
     * HSLAをHEX形式のカラーコードに変換する
     * @param {string} hsla HSLA
     * @returns HEX形式カラーコード
     */
    hslaToHex(hsla) {
      // HSLAの文字列からH, S, L, Aを抽出
      const match = hsla.match(/hsla?\(\s*(\d+),\s*(\d+)%,\s*(\d+)%,?\s*(\d*\.?\d+)?\s*\)/);
      if (!match) {
        throw new Error("Invalid HSLA format");
      }

      const hue = parseInt(match[1], 10);
      // 彩度と明度を0-1の範囲に正規化
      const saturation = parseInt(match[2], 10) / 100;
      const lightness = parseInt(match[3], 10) / 100;

      let red, green, blue;

      if (saturation === 0) {
        // 彩度が0の場合はグレースケール
        red = green = blue = lightness;
      } else {
        const hueToRgb = (huePosition) => {
          let _huePosition = huePosition;
          if (_huePosition < 0) _huePosition += 1;
          if (_huePosition > 1) _huePosition -= 1;

          const saturationAdj = lightness < 0.5 ?
            lightness * (1 + saturation) :
            lightness + saturation - lightness * saturation;
          const lightnessAdj = 2 * lightness - saturationAdj;

          if (_huePosition < 1 / 6) return lightnessAdj + (saturationAdj - lightnessAdj) * 6 * _huePosition;
          if (_huePosition < 1 / 2) return saturationAdj;
          if (_huePosition < 2 / 3) return lightnessAdj + (saturationAdj - lightnessAdj) * (2 / 3 - _huePosition) * 6;
          return lightnessAdj;
        };

        red = hueToRgb(hue / 360 + 1 / 3);
        green = hueToRgb(hue / 360);
        blue = hueToRgb(hue / 360 - 1 / 3);
      }

      // RGBを0-255の範囲に変換
      red = Math.round(red * 255);
      green = Math.round(green * 255);
      blue = Math.round(blue * 255);

      // RGBをHEX形式に変換
      const toHex = (color) => {
        const hex = color.toString(16).padStart(2, '0');
        return hex;
      };

      return `#${toHex(red)}${toHex(green)}${toHex(blue)}`;
    },

    /**
     * 太さに紐づく番号を返す
     * @param {string} thickness 太さ
     * @returns 太さに紐づく番号
     */
    getBorderWidthNumber(thickness) {
      let rtnValue = LINE_WIDTH.NORMAL;
      switch (thickness) {
        case THIN:
          rtnValue = LINE_WIDTH.THIN;
          break;
        case THICK:
          rtnValue = LINE_WIDTH.THICK;
          break;
        case NORMAL:
        default:
          rtnValue = LINE_WIDTH.NORMAL;
          break;
      }
      return rtnValue;
    },

    /**
     * スタイルに紐づく番号を返す
     * @param {string} style スタイル
     * @returns タイルに紐づく番号
     */
    getStyleNumber(style) {
      let rtnValue = STYLE.SOLID;
      switch (style) {
        case DASHED.join():
          rtnValue = STYLE.DASHED;
          break;
        case DOTTED.join():
          rtnValue = STYLE.DOTTED;
          break;
        case SOLID.join():
        default:
          rtnValue = STYLE.SOLID;
          break;
      }
      return rtnValue;
    },

    /**
     * マークボタンの処理中フラグを設定する
     * @param {boolean} bool 処理状態
     */
    async setMarkModalProcessing(bool) {
      this.markModalProcessing = bool;
    }
  },

  /**
   * 再レンダリング時のライフサイクル関数
   */
  updated() {
    if (this.tabChanged) {
      this.tabChanged = false;
      if (this.graphMediator[TAB.LIST]) {
        this.graphMediator[TAB.LIST].timelineElement.updateMarker();
      }

      if (this.graphMediator[TAB.SPLIT]) {
        this.graphMediator[TAB.SPLIT].timelineElement.updateMarker();
      }

      if (this.graphMediator[TAB.UNION]) {
        this.graphMediator[TAB.UNION].timelineElement.updateMarker();
      }

      // 計測中は以下の処理は行わない(CPU負荷軽減)
      if (!this.inProgress()) {
        if (this.graphMediator[TAB.SPLIT]) {
          this.graphMediator[TAB.SPLIT].updateComponent();
        }

        if (this.graphMediator[TAB.UNION]) {
          this.graphMediator[TAB.UNION].updateComponent();
        }
      }
    }
    this.markButtonFocus();
  },

  /**
   * コンポーネント生成時のライフサイクル関数
   */
  created() {
  },

  /**
   * コンポーネント初期レンダリング時のライフサイクル関数
   */
  mounted() {
    this.mountedLogic();
    this.$emit("measure-component-mounted", true);
  },

  /**
   * コンポーネント終了前のライフサイクル関数
   */
  beforeUnmount() {
    // 計測画面を離れる際、念のためツリーを表示に戻す
    this.setViewProcessMode(config.PROCESS_IDLING);

    this.$emit("measure-component-mounted", false);
    // ウィンドウリサイズイベントのリスナーを解除する
    window.removeEventListener('resize', this.handleResize);

    // ログイン画面遷移時は、Chart.jsの処理が途中で呼ばれるため、インスタンスを破棄しない
    if (this.$router.currentRoute.value.name !== 'login') {
      if (this.graphMediator[TAB.LIST]) {
        this.graphMediator[TAB.LIST].close();
      }

      if (this.graphMediator[TAB.UNION]) {
        this.graphMediator[TAB.UNION].close();
      }

      if (this.graphMediator[TAB.SPLIT]) {
        this.graphMediator[TAB.SPLIT].close();
      }
    }

    // データをクリアする
    this.convertedData = {};
    this.selectedGraphCheckList.splice(0);
    this.executionList = {};

    // 計測画面から離れる際に、翻訳結果のキャッシュを削除する
    new dmUtil.Cache().destroy();
  },
};
